import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Translation data
const translations = {
  english: {
    // Navigation
    tasks: 'Tasks',
    contacts: 'Contacts',
    calendar: 'Calendar',
    settings: 'Settings',
    
    // Common
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading',
    search: 'Search',
    filter: 'Filter',
    filters: 'Filters',
    show: 'Show',
    hide: 'Hide',
    clear: 'Clear',
    clearSearch: 'Clear Search',
    filtered: 'filtered',
    all: 'All',
    allCategories: 'All Categories',
    completed: 'Completed',
    pending: 'Pending',
    active: 'Active',
    overdue: 'Overdue',
    searchByTitle: 'Search by title',
    searchByNameOrPlace: 'Search by name or place',
    noContactsFound: 'No contacts found',
    noContactsFoundDesc: 'No contacts match your search criteria',
    noContactsYet: 'No contacts yet',
    addFirstContact: 'Add your first contact to get started!',
    sendEmail: 'Send Email',
    contactsExplanation: 'Link Contacts (people/companies) to tasks. View, edit, or delete them anytime.',
    
    // AddTaskModal
    createFollowUpTask: 'Create Follow-Up Task',
    addNewTask: 'Add New Task',
    followUpTask: 'Follow-up Task',
    followUpTaskLinked: 'This task will be linked to the completed parent task.',
    title: 'Title',
    dueDate: 'Due Date',
    time: 'Time',
    category: 'Category',
    priority: 'Priority',
    location: 'Location',
    contact: 'Contact',
    notes: 'Notes',
    recurring: 'Recurring',
    recurrencePattern: 'Recurrence Pattern',
    selectPattern: 'Select Pattern',
    daily: 'Daily',
    weekly: 'Weekly', 
    monthly: 'Monthly',
    yearly: 'Yearly',
    endDate: 'End Date',
    attachmentsMedia: 'Attachments & Media',
    uploadFiles: 'Upload Files',
    recordAudio: 'Record Audio',
    takePhoto: 'Take Photo',
    recurringTask: 'Recurring Task',
    uploading: 'Uploading',
    createTask: 'Create Task',
    creating: 'Creating...',
    creatingFollowUp: 'Creating Follow-up...',
    recording: 'Recording...',
    enterTaskTitle: 'Enter task title',
    addNotes: 'Add notes...',
    selectCategory: 'Select Category',
    selectContact: 'Select Contact',
    today: 'Today',
    high: 'High',
    medium: 'Medium', 
    low: 'Low',
    enterLocation: 'Enter location',
    month: 'Month',
    week: 'Week',
    taskStatusLegend: 'Task Status Legend',
    pending: 'Pending',
    overdue: 'Overdue', 
    done: 'Done',
    january: 'January',
    february: 'February',
    march: 'March',
    april: 'April', 
    may: 'May',
    june: 'June',
    july: 'July',
    august: 'August',
    september: 'September',
    october: 'October',
    november: 'November',
    december: 'December',
    editContact: 'Edit Contact',
    
    // Tasks
    addTask: 'Add Task',
    editTask: 'Edit Task',
    updateTask: 'Update Task',
    updating: 'Updating...',
    taskTitle: 'Task Title',
    taskDetails: 'Details',
    dueDate: 'Due Date',
    time: 'Time',
    category: 'Category',
    priority: 'Priority',
    location: 'Location',
    contact: 'Contact',
    notes: 'Notes',
    markComplete: 'Mark Complete',
    createFollowUpTask: 'Create Follow-Up Task',
    enterLocation: 'Enter location',
    currentAttachments: 'Current Attachments',
    addNewAttachments: 'Add New Attachments',
    loadingCategories: 'Loading categories...',
    
    // Priority levels
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    
    // Contacts
    addContact: 'Add Contact',
    editContact: 'Edit Contact',
    contactDetails: 'Details',
    contactName: 'Contact Name',
    contactNotFound: 'Contact Not Found',
    contactNotFoundDesc: 'This contact does not exist or has been deleted.',
    goBack: 'Go Back',
    email: 'Email',
    phone: 'Phone',
    company: 'Company',
    
    // Settings
    profile: 'Profile',
    preferences: 'Preferences',
    categories: 'Categories',
    subscription: 'Subscription',
    support: 'Support',
    
    // Preferences
    pushNotifications: 'Push Notifications',
    weeklyDigest: 'Weekly Email Digest',
    weeklyDigestDesc: 'Overdue tasks from past week + upcoming tasks',
    dailyDigest: 'Daily Email Digest',
    dailyDigestDesc: 'Overdue today + all tasks for tomorrow',
    dailyDigestHint: 'Sent every day at this time',
    dailyBriefPush: 'Daily Brief Push',
    dailyBriefPushDesc: 'Daily summary: Today\'s tasks, yesterday\'s overdue, tomorrow\'s high-priority',
    dailyBriefPushHint: 'Sent once daily at this time',
    taskReminders: 'Task Reminders (Push)',
    taskRemindersDesc: '1 hour before due time, filtered by priority',
    taskRemindersHint: 'Notifications sent 1 hour before task due time',
    reminderPriorities: 'Send reminders for:',
    priority: 'Priority',
    darkMode: 'Dark Mode',
    language: 'Language',
    notificationDesc: 'Receive task reminders and updates',
    darkModeDesc: 'Switch to dark theme',
    digestDay: 'Day',
    digestTime: 'Time',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    
    // Profile
    basicInformation: 'Basic Information',
    name: 'Name',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    
    // Categories
    myCategories: 'My Categories',
    addCategory: 'Add Category',
    editCategory: 'Edit Category',
    categoryName: 'Category Name',
    color: 'Color',
    noCategoriesYet: 'No categories created yet',
    createCategoriesToOrganize: 'Create categories to organize your tasks',
    createCategoriesDesc: 'Create categories to organize your tasks',
    tips: 'Tips',
    categoriesTip1: 'Categories help organize your tasks by type or project',
    categoriesTip2: 'Choose colors that make sense for each category',
    categoriesTip3: 'Categories will appear in all task creation and editing forms',
    categoriesTip4: 'You can filter tasks by category on the main Tasks page',
    
    // Messages
    taskCompleted: 'Task completed successfully!',
    taskDeleted: 'Task deleted successfully!',
    contactSaved: 'Contact saved successfully!',
    passwordChanged: 'Password changed successfully!',
    settingsSaved: 'Settings saved successfully!',
    
    // Common UI
    selectCategory: 'Select Category',
    selectContact: 'Select Contact',
    today: 'Today',
    thisWeek: 'This Week',
    next7Days: 'Next 7 Days',
    overdue: 'Overdue',
    general: 'General',
    administration: 'Administration',
    dailyBriefing: 'Daily Brief',
    // Business workspaces — task visibility
    visibility: 'Visibility',
    visibilityTeam: 'Team',
    visibilityPrivate: 'Private (only me)',
    visibilityTeamHint: 'Visible to everyone in this workspace',
    visibilityPrivateHint: 'Only you can see this task',
    getBriefing: 'Get Brief',
    voiceButton: 'Voice',
    activeTasks: 'Active Tasks',
    completedTasks: 'Completed Tasks',
    archives: 'Archives',
    archivedTasks: 'Archived Tasks',
    allArchives: 'All Archives',
    archive: 'Archive',
    restore: 'Restore',
    archiveTask: 'Archive Task',
    restoreTask: 'Restore Task',
    taskArchivedSuccessfully: 'Task archived successfully',
    taskRestoredSuccessfully: 'Task restored successfully',
    noArchivedTasks: 'No archived tasks',
    searchByTitle: 'Search by title...',
    filters: 'Filters',
    allPriorities: 'All Priorities',
    fromDate: 'From Date',
    toDate: 'To Date',
    clearAllFilters: 'Clear All Filters',
    selectTasks: 'Select Tasks',
    loadAllArchives: 'Load all archives to access search and filters',
    showingFirstTwentyTasks: 'Showing first 20 archived tasks',
    loadMoreTasks: 'Load More Tasks',
    more: 'more',
    createTask: 'Create Task',
    quickActions: 'Quick Actions',
    sendEmail: 'Send Email',
    call: 'Call',
    allContacts: 'All Contacts',
    signOut: 'Sign Out',
    signOutConfirm: 'Are you sure you want to sign out?',
    noTasksFound: 'No tasks found',
    noTasksAssigned: 'No tasks assigned to this contact',
    loadingTasks: 'Loading tasks...',
    showingFirstTasks: 'Showing first',
    loadAllTasks: 'Load all tasks to access search and filters',
    addNotes: 'Add notes...',
    completionNotes: 'Completion Notes',
    // Missing UI elements
    loadAllTasks: 'All tasks',
    aiPoweredTaskSummary: 'AI-powered task summary',
    generateBriefing: 'Generate your daily briefing',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon', 
    goodEvening: 'Good evening',
    nothingScheduled: 'Nothing scheduled',
    upcomingTasks: 'Upcoming tasks',
    contactDetails: 'Details',
    personalInfo: 'Personal Information',
    accountSecurity: 'Account & Security',
    systemPreferences: 'System Preferences',
    helpSupport: 'Help & Support',
    contentManagement: 'Content Management',
    userManagement: 'User Management',
    systemConfiguration: 'System Configuration',
    basicInfo: 'Basic Information',
    updateProfile: 'Update Profile',
    passwordSecurity: 'Password & Security',
    notificationSettings: 'Notification Settings',
    languageRegion: 'Language & Region',
    voiceTaskCreator: 'Voice Task Creator',
    speakTaskDetails: 'Speak your task details naturally',
    aiWillHelp: 'AI will help you fill in the details',
    
    // Task Reminder Settings
    taskReminders: 'Task Reminders',
    taskRemindersDesc: 'Receive reminders 1 hour before tasks with reminder enabled',
    reminderMethod: 'Reminder Method',
    email: 'Email',
    sms: 'SMS',
    usOnly: '(US only)',
    phoneNumber: 'Phone Number',
    usPhoneFormat: 'US format: +1XXXXXXXXXX or XXXXXXXXXX',
    intlPhoneFormatHint: 'Include country code (e.g. +1 for US, +33 for France). Bare 10-digit numbers are treated as US.',
    reminderInfo: 'To receive reminders, check the "Reminder" box when creating or editing a task. Reminders are sent 1 hour before the due time.',
    enableReminder: 'Enable Reminder',
    reminderDescription: 'Receive a reminder 1 hour before the due time via your preferred method (Email/SMS)',
    
    // AI Assistant phrases
    listeningPleaseAsk: 'I\'m listening. Please ask your question now.',
    pleaseAskQuestion: 'Please ask me a question about Edotask or task management.',
    voiceRecognitionError: 'Voice recognition error. Please try typing your question instead.',
    iHeardLetMeHelp: 'I heard your question. Let me help you with that.',
    
    // AI Task Creator
    aiTaskCreator: 'AI Task Creator',
    readyToHelp: 'Ready to Help',
    aiHelpCreateTask: 'I\'ll help you create a task using voice or text input.',
    voiceFeedbackQuality: 'Voice feedback quality depends on your browser\'s speech synthesis support',
    startVoice: 'Start Voice',
    text: 'Text',
    tryExample: 'Try: "Meeting with John tomorrow at 2pm" or use text input',
    
    // AI Task Creator Voice Responses
    welcomeMessage: 'Please tell me the task title, date, and time. I\'ll assign a category if you don\'t mention one, set the priority to medium, and you can edit it later.',
    letMeProcess: 'Let me process that for you...',
    processingResponse: 'Got it, let me process that...',
    createTaskNow: 'Creating your task now...',
    taskCreatedSuccess: 'Task created successfully!',
    taskCreationFailed: 'Sorry, I couldn\'t create the task. Please try again.',
    needTitleAtLeast: 'I\'d love to create the task, but I need at least a title. What should I call this task?',
    didntCatchContactName: 'I didn\'t catch the contact name. Could you say something like \'Do I have a contact named John Wilson?\'',
    troubleCheckingContacts: 'I had trouble checking your contacts. Let\'s continue with the task creation.',
    askingAboutTaskDetails: 'I was asking about your task details. Let me continue where we left off.',
    haveEverythingReady: 'I have everything I need! Ready to create your task.',
    describeTaskNaturally: 'Just describe your task naturally. I\'ll ask for any missing details.',
    perfectEnoughInfo: 'Perfect! I have enough information. Let me create your task.',
    whenIsTaskDue: 'When is "{taskTitle}" due?',
    creatingTaskWithInfo: 'Great! Creating your task with the information I have.',
    whatToChange: 'What would you like to change? You can say things like \'change the date\', \'update priority\', or \'different category\'.',
    startFreshTask: 'No problem! Let\'s start fresh. What task would you like to create?',
    perfectReadyToCreate: 'Perfect! Ready to create your task.',
    notSureWhatField: 'I\'m not sure what field you\'re referring to. Could you be more specific?',
    didntCatchRepeat: 'I didn\'t quite catch that. Could you please repeat that information?',
    couldntUnderstandTask: 'I couldn\'t understand that. Could you please describe your task again?',
    voiceNotSupported: 'Voice recognition is not supported in your browser. Please use the text input option instead.',
    needsHTTPS: 'Voice recognition requires a secure connection. Please use HTTPS or try the text input option.',
    microphoneBlocked: 'Microphone access is blocked. Please allow microphone permissions in your browser settings and try again, or use the text input option.',
    voiceCouldntStart: 'Voice recognition couldn\'t start. Please check your microphone permissions and try again, or use the text input option.',
    listeningStopped: 'Listening stopped. You can start again or use text input.',
    
    // AI Task Creator Follow-up Questions
    questionWhenDue: 'When is "{taskTitle}" due?',
    questionWhatTime: 'What time for "{taskTitle}"?',
    questionPriority: 'Priority for "{taskTitle}"? High, medium, or low?',
    questionLocation: 'Where will "{taskTitle}" happen?',
    questionCategory: 'What category is "{taskTitle}"? Work, personal, health, education, shopping, family, finance, or travel?',
    questionContact: 'Should "{taskTitle}" be linked to a contact?',
    questionNotes: 'Any notes for "{taskTitle}"?',
    questionDescription: 'More details about "{taskTitle}"?',
    questionGeneric: 'What about {field} for "{taskTitle}"?',
    thisTask: 'this task',
    
    // AI Task Creator Dynamic Responses
    iAsked: 'I asked: {question}',
    iStillNeed: 'I still need: {remaining}. Let\'s start with {nextField}.',
    okaySkipping: 'Okay, skipping that. {nextQuestion}',
    greatUsing: 'Great! Using {contactName}. {nextQuestion}',
    okayLetMeAskAgain: 'Okay, let me ask again. {question}',
    didntCatchInfoClearly: 'I didn\'t catch that information clearly. Let me ask again about {fieldName}.',
    
    // Contact Search Responses
    foundContactConfirm: 'Yes, I found {contactName} in your contacts. Would you like to link this task to {contactName}?',
    voiceContactNotFound: 'I don\'t see a contact named {contactName} in your contacts. Would you like me to continue without a contact, or would you like to specify a different name?',
    
    // Task Creation Confirmation
    perfectCreateTaskWith: 'Perfect! I\'ll create your task with the information I have: {taskTitle}',
    
    // Schema and Options Responses
    availableCategories: 'Available categories are: {categories}.',
    priorityLevels: 'Priority levels are: {priorities}.',
    
    // Contact Rejection Patterns  
    noContactNeeded: 'Understood, no contact needed. {nextAction}',
    continueWithoutContact: 'Continuing without a contact. {nextAction}',
    
    // Field Update Confirmations
    perfectSetField: 'Perfect! I\'ve set the {fieldName} to {value}.',
    excellentGotBoth: 'Excellent! I\'ve got {field1} and {field2}.',
    greatSetMultiple: 'Great! I\'ve set {fields}, and {lastField}.',
    
    // Field Help Responses
    fieldExamples: 'For {fieldDescription}, you can say: {examples}.',
    fieldOptions: 'For {fieldDescription}, choose: {options}.',
    fieldInstructions: 'Just tell me {fieldDescription}.',
    
    // AI Task Creator Progress Modal
    needMoreInfo: 'Need More Info',
    pleaseProvideRequested: 'Please provide the requested information',
    taskProgress: 'Task progress:',
    stillNeedFields: 'Still need: {fields}',
    continueAnswering: 'Continue Answering',
    
    // Field Names Translation
    fieldNames: {
      title: 'title',
      due_date: 'due date',
      due_time: 'due time', 
      time: 'time',
      priority: 'priority',
      category: 'category',
      location: 'location',
      contact: 'contact',
      notes: 'notes',
      description: 'description'
    },
    
    // Daily Briefing phrases  
    goodMorningBriefing: 'Good morning! Here\'s your daily briefing',
    goodAfternoonBriefing: 'Good afternoon! Here\'s your daily briefing',
    goodEveningBriefing: 'Good evening! Here\'s your daily briefing',
    
    // Task List Elements (Home Page)
    showingFirstTwentyTasks: 'Showing first 20 tasks',
    followUpOf: 'Follow-up of',
    recurring: 'Recurring',
    instance: 'Instance',
    followUps: 'follow-ups',
    filtered: '(Filtered)',
    parentTask: 'Parent Task',
    viewFollowUpTasks: 'View follow-up tasks',
    clickToFilterRecurring: 'Click to filter recurring group',
    showAllTasks: 'show all tasks',
    
    // Task Details Page
    taskNotFound: 'Task Not Found',
    taskNotFoundDesc: 'This task does not exist or has been deleted.',
    priorityLabel: 'Priority',
    completedLabel: 'Completed',
    jumpToRecurringSection: 'Jump to recurring tasks section below',
    completionInfo: 'Click to complete. Add file/note if needed. Completed tasks can\'t be edited, only deleted.',
    followUpTaskInfo: 'Create related tasks or next steps to keep work connected.',
    thisTaskHasFollowUps: 'This task has follow-up tasks',
    
    // Calendar Page
    monthNames: {
      january: 'January',
      february: 'February', 
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December'
    },
    weekDays: {
      sun: 'Sun',
      mon: 'Mon', 
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat'
    },
    legend: 'Legend',
    hasPendingTasks: 'Has Pending Tasks',
    hasOverdueTasks: 'Has Overdue Tasks', 
    hasCompletedTasks: 'Has Completed Tasks',
    tasksFor: 'Tasks for',
    noTasksForDate: 'No tasks for this date',
    loadingCalendar: 'Loading Calendar',
    preparingTasks: 'Preparing your tasks...',
    loadingYourCalendar: 'Loading your calendar...',
    
    // Contacts
    searchContacts: 'Search contacts...',
    loadingContacts: 'Loading contacts...',
    contactInformation: 'Contact Information',
    place: 'Place',
    address: 'Address',
    clickToOpenMaps: 'Click to open in Google Maps',
    enterContactName: 'Enter contact name',
    contactEmailPlaceholder: 'contact@example.com',
    phonePlaceholder: '+1 (555) 123-4567',
    placeName: 'Place name',
    enterFullAddress: 'Enter full address',
    createContact: 'Create Contact',
    
    // Settings Page
    manageYourAccount: 'Manage your account',
    signOut: 'Sign Out',
    areYouSureSignOut: 'Are you sure you want to sign out?',
    
    // Profile Tab
    basicInformation: 'Basic Information',
    updateProfile: 'Update Profile',
    password: 'Password',
    cancel: 'Cancel',
    change: 'Change',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmNewPassword: 'Confirm New Password',
    changePassword: 'Change Password',
    profileUpdatedSuccessfully: 'Profile updated successfully!',
    passwordsDoNotMatch: 'New passwords do not match',
    passwordChangedSuccessfully: 'Password changed successfully!',
    
    // Preferences Tab  
    preferences: 'Preferences',
    pushNotifications: 'Push Notifications',
    receiveTaskReminders: 'Receive task reminders and updates',
    emailDigest: 'Email Digest',
    weeklySummaryTasks: 'Weekly summary of your tasks',
    darkMode: 'Dark Mode',
    switchToDarkTheme: 'Switch to dark theme',
    
    // Categories Tab
    addNewCategory: 'Add New Category',
    editCategory: 'Edit Category',
    categoryName: 'Category Name',
    enterCategoryName: 'Enter category name',
    color: 'Color',
    addCategory: 'Add Category',
    updateCategory: 'Update Category',
    myCategories: 'My Categories',
    noCategoriesCreated: 'No categories created yet',
    createCategoriesToOrganize: 'Create categories to organize your tasks',
    deleteCategoryConfirm: 'Are you sure you want to delete the category',
    failedToSaveCategory: 'Failed to save category. Please try again.',
    failedToDeleteCategory: 'Failed to delete category. Please try again.',
    
    // Subscription Tab
    currentPlan: 'Current Plan',
    plan: 'Plan',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    upgradeToPro: 'Upgrade to Pro',
    usageStatistics: 'Usage Statistics',
    tasksCreated: 'Tasks Created',
    storageUsed: 'Storage Used',
    freePlan: 'Free Plan',
    statusActive: 'Status: Active',
    billingHistory: 'Billing History',
    noBillingHistory: 'No billing history available',
    upgradeToSeeBilling: 'Upgrade to Pro to see billing information',
    
    // Support Tab
    helpSupport: 'Help & Support',
    helpCenter: 'Help Center',
    browseFrequentlyAsked: 'Browse frequently asked questions',
    contactSupport: 'Contact Support',
    sendFeedback: 'Send Feedback',
    helpUsImprove: 'Help us improve Edotask',
    appInformation: 'App Information',
    version: 'Version',
    lastUpdated: 'Last Updated',
    allSystemsOperational: 'All Systems Operational',
    aiHelpAssistant: 'AI Help Assistant',
    askMeAnything: 'Ask me anything about Edotask',
    voiceEnabled: 'VOICE ENABLED',
    legal: 'Legal',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    cookiePolicy: 'Cookie Policy',
    
    // Daily Briefing Placeholder
    readyForDailyBriefing: 'Ready for Your Daily Briefing',
    getAiPoweredSummary: 'Get an AI-powered summary of ALL your tasks due today, including priorities and regular tasks',
    clickGetBriefingInstructions: 'Click "Get Briefing" to see text, then "Voice" to hear it spoken',
    unableToGenerateBriefing: 'Unable to generate your daily briefing.',
    
    // Task Conflict Modal
    timeConflictCannotSave: 'Time Conflict - Cannot Save',
    schedulingConflictWarning: 'Scheduling Conflict Warning',
    exactTimeConflictDetected: 'Exact Time Conflict Detected',
    exactTimeConflictMessage: 'You already have a task scheduled at the exact same date and time. Please change the time to continue.',
    nearbyTasksDetected: 'Nearby Tasks Detected',
    nearbyTasksMessage: 'You have {count} task{plural} scheduled within 2 hours of this time.',
    at: 'at',
    editTime: 'Edit Time',
    saveAnyway: 'Save Anyway',
    
    // Calendar tooltips
    hasOverdueTasksTooltip: 'Has overdue tasks',
    hasPendingTasksTooltip: 'Has pending tasks', 
    hasCompletedTasksTooltip: 'Has completed tasks',
    
    // Missing translations
    addNewContact: 'Add new contact',
    noContactsFound: 'No contacts found',
    noContactsYet: 'No contacts yet',
    noContactsFoundDesc: 'No contacts match',
    addFirstContact: 'Add your first contact to get started!',
    
    // Auth
    login: 'Login',
    register: 'Register',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    forgotPassword: 'Forgot Password',
    resetPassword: 'Reset Password',

    // Tasks list hints & actions
    loadAllTasksHint: 'Showing 20 — tap All Tasks to search and filter.',

    // Contacts page
    importFromPhone: 'Import from phone',
    importingProgress: 'Importing…',
    addContactBtn: 'Add contact',
    importTipText: 'Tip: On your phone, open Contacts → Share/Export → choose vCard (.vcf) or CSV, then upload it here.',
    selectBtn: 'Select',
    selectAll: 'Select all',
    clearBtn: 'Clear',
    deletingProgress: 'Deleting…',
    selectedCountSuffix: 'selected',

    // Preferences / Notifications
    pushNotification: 'Push notification',
    browserNotificationPermission: 'Browser notifications need your permission to work.',
    enableNotifications: 'Enable Notifications',
    enabledSendTest: '✅ Enabled - Send Test',
    statusLabel: 'Status:',
    statusEnabled: '✅ Enabled',
    statusBlocked: '❌ Blocked',
    statusNotEnabled: '⚠️ Not Enabled',
    statusNotAvailablePreview: '⚠️ Not Available in Preview',

    // Settings hubs
    preferencesHubSubtitle: 'Language, theme, reminders & digests',
    categoriesHubSubtitle: 'Customize your task categories',

    // Administration
    administrationPanel: 'Administration Panel',
    systemUserControlsSubtitle: 'System management & user controls',
    adminBtn: 'Admin',

    // Settings hub & sub-section subtitles
    settingsTitle: 'Settings',
    settingsHubSubtitle: 'Manage your account',
    profileHubSubtitle: 'Name, email, and password',
    subscriptionHubSubtitle: 'Plan, billing, and invoices',
    supportHubSubtitle: 'Help center, feedback, legal',
    pickASection: 'Pick a section',
    chooseACategoryHelp: 'Choose a category on the left to manage your account.',

    // Sharing
    share: 'Share',
    shareThisTask: 'Share this task',
    shareThisContact: 'Share this contact',
    sendAsCalendarInvite: 'Send as calendar invite',
    sendAsCalendarInviteDesc: 'Opens in Google, Outlook or Apple Calendar',
    copyShareLink: 'Copy share link',
    copyShareLinkDesc: 'Anyone with the link can view, read-only',
    sendAsVCard: 'Send as vCard (.vcf)',
    sendAsVCardDesc: 'Imports directly into the recipient\'s Contacts app',
    sendAsPdfCard: 'Send as PDF business card',
    sendAsPdfCardDesc: 'A polished one-page PDF ready to attach in email',
    linkCopied: 'Link copied to clipboard',
    linkCopyFailed: 'Could not copy link — please try again',
    sharedReadOnly: 'Shared read-only',
    openInEdotask: 'Open in Edotask',
    poweredByEdotask: 'Powered by Edotask',
    sharedTaskExpired: 'This share link is no longer valid',
    sharedTaskExpiredDesc: 'The owner has revoked it or the task was removed.',

    // Account deletion (Settings → Profile → Danger Zone)
    dangerZone: 'Danger Zone',
    dangerZoneSubtitle: 'Irreversible actions for this account.',
    deleteAccount: 'Delete account',
    deleteAccountHint: 'Permanently remove your account, tasks, contacts and settings.',
    deleteAccountTitle: 'Delete your account',
    deleteAccountBtn: 'Delete my account',
    deleteAccountProgress: 'Deleting…',
    deleteAccountWarnTitle: 'This action cannot be undone.',
    deleteAccountWarnBody: 'All your tasks, contacts, attachments, share links and settings will be permanently deleted. Any active subscription will be cancelled.',
    deleteAccountPasswordLabel: 'Confirm your password',
    deleteAccountTypeDeleteLabel: 'Type DELETE to confirm',

    // Post-signup email verification banner (QA-001)
    signupCheckEmailTitle: 'Almost there — check your email',

    // Resend verification email (QA-001 follow-up)
    resendVerifyPrompt: "Didn't receive it?",
    resendVerifyCta: 'Resend email',
    resendVerifySending: 'Sending…',
    resendVerifySent: 'Sent — please check your inbox again.',
    resendVerifyCooldown: 'Sent',
    resendVerifyError: "Couldn't resend right now. Please try again in a minute.",

    signupCheckEmailBody: 'A confirmation email has been sent. Please open your inbox (and the Spam folder just in case) and click the link to activate your account.',




    // Misc strings
    weekDaySun: 'Sun',
    weekDayMon: 'Mon',
    weekDayTue: 'Tue',
    weekDayWed: 'Wed',
    weekDayThu: 'Thu',
    weekDayFri: 'Fri',
    weekDaySat: 'Sat',
    youAreProUser: 'You\u2019re a Pro user',
    privacyDataTitle: 'Privacy & Data',
    privacyDataDesc: 'Manage what\'s stored on this device. Your account on the server is never affected.',
    micTip: 'Tip: tap the mic on your keyboard to dictate instead of typing.',
    appLastUpdated: 'January 2025',
    update: 'Update',

    // Notifications
    notifications: 'Notifications',
    noNotifications: "You're all caught up.",
    teamInvitations: 'Team invitations',
    dueSoon: 'Due soon',
    invitedAsRole: 'Invited as',
    dueNow: 'Due now',
    inLabel: 'in',
    minutesShort: 'min',
    newLabel: 'new',

    // Organization invites
    inviteTeammate: 'Invite a teammate',
    inviteFreeCopy: "Your teammate joins for free — you cover their seat. Your $10/month plan includes you + 1 teammate; each extra teammate adds $5/month when they accept."
  },
  
  spanish: {
    // Navigation
    tasks: 'Tareas',
    contacts: 'Contactos',
    calendar: 'Calendario',
    settings: 'Configuración',
    
    // Common
    add: 'Añadir',
    edit: 'Editar',
    delete: 'Eliminar',
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'Cargando',
    search: 'Buscar',
    filter: 'Filtrar',
    filters: 'Filtros',
    show: 'Mostrar',
    hide: 'Ocultar',
    clear: 'Limpiar',
    clearSearch: 'Limpiar Búsqueda',
    filtered: 'filtradas',
    all: 'Todos',
    allCategories: 'Todas las Categorías',
    completed: 'Completadas',
    pending: 'Pendientes',
    active: 'Activas',
    overdue: 'Vencidas',
    searchByTitle: 'Buscar por título',
    searchByNameOrPlace: 'Buscar por nombre o lugar',
    noContactsFound: 'No se encontraron contactos',
    noContactsFoundDesc: 'Ningún contacto coincide con tu búsqueda',
    noContactsYet: 'Aún no hay contactos',
    addFirstContact: '¡Añade tu primer contacto para comenzar!',
    sendEmail: 'Enviar Email',
    contactsExplanation: 'Vincula Contactos (personas/empresas) a tareas. Visualiza, edita o elimina en cualquier momento.',
    
    // Tasks
    addTask: 'Añadir Tarea',
    editTask: 'Editar Tarea',
    updateTask: 'Actualizar Tarea',
    updating: 'Actualizando...',
    taskTitle: 'Título de la Tarea',
    taskDetails: 'Detalles',
    dueDate: 'Fecha de Vencimiento',
    time: 'Hora',
    category: 'Categoría',
    priority: 'Prioridad',
    location: 'Ubicación',
    contact: 'Contacto',
    notes: 'Notas',
    markComplete: 'Marcar Completada',
    createFollowUpTask: 'Crear Tarea de Seguimiento',
    enterLocation: 'Ingresar ubicación',
    currentAttachments: 'Archivos Adjuntos Actuales',
    addNewAttachments: 'Añadir Nuevos Archivos Adjuntos',
    loadingCategories: 'Cargando categorías...',
    
    // Priority levels
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    
    // Contacts
    addContact: 'Añadir Contacto',
    editContact: 'Editar Contacto',
    contactDetails: 'Detalles',
    contactName: 'Nombre del Contacto',
    contactNotFound: 'Contacto No Encontrado',
    contactNotFoundDesc: 'Este contacto no existe o ha sido eliminado.',
    goBack: 'Volver',
    email: 'Correo Electrónico',
    phone: 'Teléfono',
    company: 'Empresa',
    
    // Settings
    profile: 'Perfil',
    preferences: 'Preferencias',
    categories: 'Categorías',
    subscription: 'Suscripción',
    support: 'Soporte',
    
    // Preferences
    pushNotifications: 'Notificaciones Push',
    weeklyDigest: 'Resumen Semanal por Correo',
    weeklyDigestDesc: 'Tareas vencidas de la semana pasada + próximas tareas',
    dailyDigest: 'Resumen Diario por Correo',
    dailyDigestDesc: 'Tareas vencidas hoy + todas las tareas de mañana',
    dailyDigestHint: 'Enviado todos los días a esta hora',
    dailyBriefPush: 'Notificación Diaria Push',
    dailyBriefPushDesc: 'Resumen diario: Tareas de hoy, vencidas de ayer, alta prioridad de mañana',
    dailyBriefPushHint: 'Enviado una vez al día a esta hora',
    taskReminders: 'Recordatorios de Tareas (Push)',
    taskRemindersDesc: '1 hora antes de la hora de vencimiento, filtrado por prioridad',
    taskRemindersHint: 'Notificaciones enviadas 1 hora antes de la hora de vencimiento',
    // Task reminder method picker (added 2026-02-22 to fix missing ES strings)
    reminderMethod: 'Método de recordatorio',
    email: 'Correo electrónico',
    sms: 'SMS',
    usOnly: '(solo EE. UU.)',
    phoneNumber: 'Número de teléfono',
    usPhoneFormat: 'Formato EE. UU.: +1XXXXXXXXXX o XXXXXXXXXX',
    intlPhoneFormatHint: 'Incluye el código de país (por ejemplo, +1 para EE. UU., +34 para España). Los números de 10 dígitos sin prefijo se tratan como de EE. UU.',
    reminderInfo: 'Para recibir recordatorios, marca la casilla "Recordatorio" al crear o editar una tarea. Los recordatorios se envían 1 hora antes de la hora de vencimiento.',
    enableReminder: 'Activar recordatorio',
    reminderDescription: 'Recibe un recordatorio 1 hora antes de la hora de vencimiento por el método que prefieras (correo o SMS)',
    reminderPriorities: 'Enviar recordatorios para:',
    priority: 'Prioridad',
    darkMode: 'Modo Oscuro',
    language: 'Idioma',
    notificationDesc: 'Recibir recordatorios y actualizaciones de tareas',
    darkModeDesc: 'Cambiar a tema oscuro',
    digestDay: 'Día',
    digestTime: 'Hora',
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
    
    // Profile
    basicInformation: 'Información Básica',
    name: 'Nombre',
    changePassword: 'Cambiar Contraseña',
    currentPassword: 'Contraseña Actual',
    newPassword: 'Nueva Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    
    // Categories
    myCategories: 'Mis Categorías',
    addCategory: 'Añadir Categoría',
    editCategory: 'Editar Categoría',
    categoryName: 'Nombre de la Categoría',
    color: 'Color',
    noCategoriesYet: 'Aún no hay categorías creadas',
    createCategoriesToOrganize: 'Crea categorías para organizar tus tareas',
    createCategoriesDesc: 'Crea categorías para organizar tus tareas',
    tips: 'Consejos',
    categoriesTip1: 'Las categorías ayudan a organizar tus tareas por tipo o proyecto',
    categoriesTip2: 'Elige colores que tengan sentido para cada categoría',
    categoriesTip3: 'Las categorías aparecerán en todos los formularios de creación y edición de tareas',
    categoriesTip4: 'Puedes filtrar tareas por categoría en la página principal de Tareas',
    
    // Messages
    taskCompleted: '¡Tarea completada con éxito!',
    taskDeleted: '¡Tarea eliminada con éxito!',
    contactSaved: '¡Contacto guardado con éxito!',
    passwordChanged: '¡Contraseña cambiada con éxito!',
    settingsSaved: '¡Configuración guardada con éxito!',
    
    // Common UI
    selectCategory: 'Seleccionar Categoría',
    selectContact: 'Seleccionar Contacto',
    today: 'Hoy',
    thisWeek: 'Esta Semana',
    next7Days: 'Próximos 7 Días',
    overdue: 'Vencidas',
    general: 'General',
    administration: 'Administración',
    dailyBriefing: 'Punto Diario',
    // Business workspaces — task visibility
    visibility: 'Visibilidad',
    visibilityTeam: 'Equipo',
    visibilityPrivate: 'Privada (sólo yo)',
    visibilityTeamHint: 'Visible para todos en este espacio',
    visibilityPrivateHint: 'Sólo tú puedes ver esta tarea',
    getBriefing: 'El Punto',
    voiceButton: 'Voz',
    activeTasks: 'Tareas Activas',
    completedTasks: 'Tareas Completadas',
    archives: 'Archivos',
    archivedTasks: 'Tareas Archivadas',
    allArchives: 'Todos los Archivos',
    archive: 'Archivar',
    restore: 'Restaurar',
    archiveTask: 'Archivar Tarea',
    restoreTask: 'Restaurar Tarea',
    taskArchivedSuccessfully: 'Tarea archivada exitosamente',
    taskRestoredSuccessfully: 'Tarea restaurada exitosamente',
    noArchivedTasks: 'No hay tareas archivadas',
    searchByTitle: 'Buscar por título...',
    filters: 'Filtros',
    allPriorities: 'Todas las Prioridades',
    fromDate: 'Fecha Desde',
    toDate: 'Fecha Hasta',
    clearAllFilters: 'Limpiar Todos los Filtros',
    selectTasks: 'Seleccionar Tareas',
    loadAllArchives: 'Cargar todos los archivos para acceder a búsqueda y filtros',
    showingFirstTwentyTasks: 'Mostrando las primeras 20 tareas archivadas',
    loadMoreTasks: 'Cargar Más Tareas',
    more: 'más',
    createTask: 'Crear Tarea',
    quickActions: 'Acciones Rápidas',
    sendEmail: 'Enviar Email',
    call: 'Llamar',
    allContacts: 'Todos los Contactos',
    signOut: 'Cerrar Sesión',
    signOutConfirm: '¿Estás seguro de que quieres cerrar sesión?',
    noTasksFound: 'No se encontraron tareas',
    noTasksAssigned: 'No hay tareas asignadas a este contacto',
    loadingTasks: 'Cargando tareas...',
    showingFirstTasks: 'Mostrando primeras',
    loadAllTasks: 'Cargar todas las tareas para acceder a búsqueda y filtros',
    addNotes: 'Agregar notas...',
    completionNotes: 'Notas de Finalización',
    // Missing UI elements
    loadAllTasks: 'Ver Todas',
    aiPoweredTaskSummary: 'Resumen de tareas impulsado por IA',
    generateBriefing: 'Genera tu resumen diario',
    goodMorning: 'Buenos días',
    goodAfternoon: 'Buenas tardes',
    goodEvening: 'Buenas noches',
    nothingScheduled: 'Nada programado',
    upcomingTasks: 'Tareas próximas',
    contactDetails: 'Detalles',
    personalInfo: 'Información Personal',
    accountSecurity: 'Cuenta y Seguridad',
    systemPreferences: 'Preferencias del Sistema',
    helpSupport: 'Ayuda y Soporte',
    contentManagement: 'Gestión de Contenido',
    userManagement: 'Gestión de Usuarios',
    systemConfiguration: 'Configuración del Sistema',
    basicInfo: 'Información Básica',
    updateProfile: 'Actualizar Perfil',
    passwordSecurity: 'Contraseña y Seguridad',
    notificationSettings: 'Configuración de Notificaciones',
    languageRegion: 'Idioma y Región',
    voiceTaskCreator: 'Creador de Tareas por Voz',
    speakTaskDetails: 'Habla los detalles de tu tarea naturalmente',
    aiWillHelp: 'La IA te ayudará a completar los detalles',
    
    // AI Assistant phrases
    listeningPleaseAsk: 'Estoy escuchando. Por favor, haz tu pregunta ahora.',
    pleaseAskQuestion: 'Por favor, hazme una pregunta sobre Edotask o gestión de tareas.',
    voiceRecognitionError: 'Error de reconocimiento de voz. Por favor, intenta escribir tu pregunta.',
    iHeardLetMeHelp: 'Escuché tu pregunta. Déjame ayudarte con eso.',
    
    // AI Task Creator
    aiTaskCreator: 'Creador de Tareas IA',
    readyToHelp: 'Listo para Ayudar',
    aiHelpCreateTask: 'Te ayudaré a crear una tarea usando entrada de voz o texto.',
    voiceFeedbackQuality: 'La calidad de la respuesta de voz depende del soporte de síntesis de voz de tu navegador',
    startVoice: 'Iniciar Voz',
    text: 'Texto',
    tryExample: 'Prueba: "Reunión con Juan mañana a las 2pm" o usa entrada de texto',
    
    // AI Task Creator Voice Responses
    welcomeMessage: 'Por favor dime el título de la tarea, fecha y hora. Asignaré una categoría si no mencionas una, estableceré la prioridad en media, y podrás editarla después.',
    letMeProcess: 'Déjame procesar eso por ti...',
    processingResponse: 'Entendido, déjame procesar eso...',
    createTaskNow: 'Creando tu tarea ahora...',
    taskCreatedSuccess: '¡Tarea creada exitosamente!',
    taskCreationFailed: 'Lo siento, no pude crear la tarea. Por favor inténtalo de nuevo.',
    needTitleAtLeast: 'Me encantaría crear la tarea, pero necesito al menos un título. ¿Cómo debería llamar a esta tarea?',
    didntCatchContactName: 'No escuché el nombre del contacto. ¿Podrías decir algo como "¿Tengo un contacto llamado Juan Wilson?"',
    troubleCheckingContacts: 'Tuve problemas revisando tus contactos. Continuemos con la creación de la tarea.',
    askingAboutTaskDetails: 'Estaba preguntando sobre los detalles de tu tarea. Continuemos donde lo dejamos.',
    haveEverythingReady: '¡Tengo todo lo que necesito! Listo para crear tu tarea.',
    describeTaskNaturally: 'Solo describe tu tarea naturalmente. Te preguntaré por cualquier detalle faltante.',
    perfectEnoughInfo: '¡Perfecto! Tengo suficiente información. Déjame crear tu tarea.',
    whenIsTaskDue: '¿Cuándo vence "{taskTitle}"?',
    creatingTaskWithInfo: '¡Excelente! Creando tu tarea con la información que tengo.',
    whatToChange: '¿Qué te gustaría cambiar? Puedes decir cosas como "cambiar la fecha", "actualizar prioridad", o "categoría diferente".',
    startFreshTask: '¡Sin problema! Empecemos de nuevo. ¿Qué tarea te gustaría crear?',
    perfectReadyToCreate: '¡Perfecto! Listo para crear tu tarea.',
    notSureWhatField: 'No estoy seguro a qué campo te refieres. ¿Podrías ser más específico?',
    didntCatchRepeat: 'No escuché bien eso. ¿Podrías repetir esa información por favor?',
    couldntUnderstandTask: 'No pude entender eso. ¿Podrías describir tu tarea de nuevo por favor?',
    voiceNotSupported: 'El reconocimiento de voz no es compatible con tu navegador. Por favor usa la opción de entrada de texto.',
    needsHTTPS: 'El reconocimiento de voz requiere una conexión segura. Por favor usa HTTPS o prueba la opción de entrada de texto.',
    microphoneBlocked: 'El acceso al micrófono está bloqueado. Por favor permite los permisos del micrófono en la configuración de tu navegador e inténtalo de nuevo, o usa la opción de entrada de texto.',
    voiceCouldntStart: 'El reconocimiento de voz no pudo iniciar. Por favor revisa los permisos de tu micrófono e inténtalo de nuevo, o usa la opción de entrada de texto.',
    listeningStopped: 'Escucha detenida. Puedes empezar de nuevo o usar entrada de texto.',
    
    // AI Task Creator Follow-up Questions
    questionWhenDue: '¿Cuándo vence "{taskTitle}"?',
    questionWhatTime: '¿A qué hora "{taskTitle}"?',
    questionPriority: '¿Prioridad para "{taskTitle}"? ¿Alta, media o baja?',
    questionLocation: '¿Dónde sucederá "{taskTitle}"?',
    questionCategory: '¿Qué categoría es "{taskTitle}"? ¿Trabajo, personal, salud, educación, compras, familia, finanzas o viajes?',
    questionContact: '¿Debería "{taskTitle}" estar vinculada a un contacto?',
    questionNotes: '¿Alguna nota para "{taskTitle}"?',
    questionDescription: '¿Más detalles sobre "{taskTitle}"?',
    questionGeneric: '¿Qué hay sobre {field} para "{taskTitle}"?',
    thisTask: 'esta tarea',
    
    // AI Task Creator Dynamic Responses
    iAsked: 'Pregunté: {question}',
    iStillNeed: 'Aún necesito: {remaining}. Empecemos con {nextField}.',
    okaySkipping: 'Está bien, saltando eso. {nextQuestion}',
    greatUsing: '¡Genial! Usando {contactName}. {nextQuestion}',
    okayLetMeAskAgain: 'Está bien, déjame preguntar de nuevo. {question}',
    didntCatchInfoClearly: 'No capté esa información claramente. Déjame preguntar de nuevo sobre {fieldName}.',
    
    // Contact Search Responses
    foundContactConfirm: 'Sí, encontré a {contactName} en tus contactos. ¿Te gustaría vincular esta tarea a {contactName}?',
    voiceContactNotFound: 'No veo un contacto llamado {contactName} en tus contactos. ¿Te gustaría que continúe sin un contacto, o quieres especificar un nombre diferente?',
    
    // Task Creation Confirmation
    perfectCreateTaskWith: '¡Perfecto! Crearé tu tarea con la información que tengo: {taskTitle}',
    
    // Schema and Options Responses
    availableCategories: 'Las categorías disponibles son: {categories}.',
    priorityLevels: 'Los niveles de prioridad son: {priorities}.',
    
    // Contact Rejection Patterns  
    noContactNeeded: 'Entendido, no se necesita contacto. {nextAction}',
    continueWithoutContact: 'Continuando sin contacto. {nextAction}',
    
    // Field Update Confirmations
    perfectSetField: '¡Perfecto! He establecido {fieldName} como {value}.',
    excellentGotBoth: '¡Excelente! Tengo {field1} y {field2}.',
    greatSetMultiple: '¡Genial! He establecido {fields}, y {lastField}.',
    
    // Field Help Responses
    fieldExamples: 'Para {fieldDescription}, puedes decir: {examples}.',
    fieldOptions: 'Para {fieldDescription}, elige: {options}.',
    fieldInstructions: 'Solo dime {fieldDescription}.',
    
    // AI Task Creator Progress Modal
    needMoreInfo: 'Necesito Más Información',
    pleaseProvideRequested: 'Por favor proporciona la información solicitada',
    taskProgress: 'Progreso de la tarea:',
    stillNeedFields: 'Aún necesito: {fields}',
    continueAnswering: 'Continuar Respondiendo',
    
    // Field Names Translation
    fieldNames: {
      title: 'título',
      due_date: 'fecha de vencimiento',
      due_time: 'hora de vencimiento',
      time: 'hora', 
      priority: 'prioridad',
      category: 'categoría',
      location: 'ubicación',
      contact: 'contacto',
      notes: 'notas',
      description: 'descripción'
    },
    
    // Daily Briefing phrases
    goodMorningBriefing: '¡Buenos días! Aquí tienes tu resumen diario',
    goodAfternoonBriefing: '¡Buenas tardes! Aquí tienes tu resumen diario',
    goodEveningBriefing: '¡Buenas noches! Aquí tienes tu resumen diario',
    
    // Task List Elements (Home Page)
    showingFirstTwentyTasks: 'Mostrando las primeras 20 tareas',
    followUpOf: 'Seguimiento de',
    recurring: 'Recurrente',
    instance: 'Instancia',
    followUps: 'seguimientos',
    filtered: '(Filtrado)',
    parentTask: 'Tarea Padre',
    viewFollowUpTasks: 'Ver tareas de seguimiento',
    clickToFilterRecurring: 'Haz clic para filtrar grupo recurrente',
    showAllTasks: 'mostrar todas las tareas',
    
    // Task Details Page
    taskNotFound: 'Tarea No Encontrada',
    taskNotFoundDesc: 'Esta tarea no existe o ha sido eliminada.',
    priorityLabel: 'Prioridad',
    completedLabel: 'Completada',
    jumpToRecurringSection: 'Ir a la sección de tareas recurrentes abajo',
    completionInfo: 'Haz clic para completar. Agrega archivo/nota si es necesario. Las tareas completadas no se pueden editar, solo eliminar.',
    followUpTaskInfo: 'Crea tareas relacionadas o próximos pasos para mantener el trabajo conectado.',
    thisTaskHasFollowUps: 'Esta tarea tiene tareas de seguimiento',
    
    // Calendar Page
    monthNames: {
      january: 'Enero',
      february: 'Febrero', 
      march: 'Marzo',
      april: 'Abril',
      may: 'Mayo',
      june: 'Junio',
      july: 'Julio',
      august: 'Agosto',
      september: 'Septiembre',
      october: 'Octubre',
      november: 'Noviembre',
      december: 'Diciembre'
    },
    weekDays: {
      sun: 'Dom',
      mon: 'Lun', 
      tue: 'Mar',
      wed: 'Mié',
      thu: 'Jue',
      fri: 'Vie',
      sat: 'Sáb'
    },
    legend: 'Leyenda',
    hasPendingTasks: 'Tiene Tareas Pendientes',
    hasOverdueTasks: 'Tiene Tareas Vencidas', 
    hasCompletedTasks: 'Tiene Tareas Completadas',
    tasksFor: 'Tareas para',
    noTasksForDate: 'No hay tareas para esta fecha',
    loadingCalendar: 'Cargando Calendario',
    preparingTasks: 'Preparando tus tareas...',
    loadingYourCalendar: 'Cargando tu calendario...',
    
    // Contacts
    searchContacts: 'Buscar contactos...',
    loadingContacts: 'Cargando contactos...',
    contactInformation: 'Información del Contacto',
    place: 'Lugar',
    address: 'Dirección',
    clickToOpenMaps: 'Haz clic para abrir en Google Maps',
    enterContactName: 'Ingresa nombre del contacto',
    contactEmailPlaceholder: 'contacto@ejemplo.com',
    phonePlaceholder: '+1 (555) 123-4567',
    placeName: 'Nombre del lugar',
    enterFullAddress: 'Ingresa dirección completa',
    createContact: 'Crear Contacto',
    
    // Settings Page
    manageYourAccount: 'Gestionar tu cuenta',
    signOut: 'Cerrar Sesión',
    areYouSureSignOut: '¿Estás seguro de que quieres cerrar sesión?',
    
    // Profile Tab
    basicInformation: 'Información Básica',
    updateProfile: 'Actualizar Perfil',
    password: 'Contraseña',
    cancel: 'Cancelar',
    change: 'Cambiar',
    currentPassword: 'Contraseña Actual',
    newPassword: 'Nueva Contraseña',
    confirmNewPassword: 'Confirmar Nueva Contraseña',
    changePassword: 'Cambiar Contraseña',
    profileUpdatedSuccessfully: '¡Perfil actualizado con éxito!',
    passwordsDoNotMatch: 'Las nuevas contraseñas no coinciden',
    passwordChangedSuccessfully: '¡Contraseña cambiada con éxito!',
    
    // Preferences Tab  
    preferences: 'Preferencias',
    pushNotifications: 'Notificaciones Push',
    receiveTaskReminders: 'Recibir recordatorios y actualizaciones de tareas',
    emailDigest: 'Resumen por Correo',
    weeklySummaryTasks: 'Resumen semanal de tus tareas',
    darkMode: 'Modo Oscuro',
    switchToDarkTheme: 'Cambiar a tema oscuro',
    
    // Categories Tab
    addNewCategory: 'Añadir Nueva Categoría',
    editCategory: 'Editar Categoría',
    categoryName: 'Nombre de la Categoría',
    enterCategoryName: 'Ingresa el nombre de la categoría',
    color: 'Color',
    addCategory: 'Añadir Categoría',
    updateCategory: 'Actualizar Categoría',
    myCategories: 'Mis Categorías',
    noCategoriesCreated: 'Aún no hay categorías creadas',
    createCategoriesToOrganize: 'Crea categorías para organizar tus tareas',
    deleteCategoryConfirm: '¿Estás seguro de que quieres eliminar la categoría',
    failedToSaveCategory: 'Error al guardar categoría. Por favor, inténtalo de nuevo.',
    failedToDeleteCategory: 'Error al eliminar categoría. Por favor, inténtalo de nuevo.',
    
    // Subscription Tab
    currentPlan: 'Plan Actual',
    plan: 'Plan',
    status: 'Estado',
    active: 'Activo',
    inactive: 'Inactivo',
    upgradeToPro: 'Actualizar a Pro',
    usageStatistics: 'Estadísticas de Uso',
    tasksCreated: 'Tareas Creadas',
    storageUsed: 'Almacenamiento Usado',
    freePlan: 'Plan Gratuito',
    statusActive: 'Estado: Activo',
    billingHistory: 'Historial de Facturación',
    noBillingHistory: 'No hay historial de facturación disponible',
    upgradeToSeeBilling: 'Actualiza a Pro para ver información de facturación',
    
    // Support Tab
    helpSupport: 'Ayuda y Soporte',
    helpCenter: 'Centro de Ayuda',
    browseFrequentlyAsked: 'Navegar preguntas frecuentes',
    contactSupport: 'Contactar Soporte',
    sendFeedback: 'Enviar Comentarios',
    helpUsImprove: 'Ayúdanos a mejorar Edotask',
    appInformation: 'Información de la App',
    version: 'Versión',
    lastUpdated: 'Última Actualización',
    allSystemsOperational: 'Todos los Sistemas Operacionales',
    aiHelpAssistant: 'Asistente de Ayuda IA',
    askMeAnything: 'Pregúntame cualquier cosa sobre Edotask',
    voiceEnabled: 'VOZ HABILITADA',
    legal: 'Legal',
    privacyPolicy: 'Política de Privacidad',
    termsOfService: 'Términos de Servicio',
    cookiePolicy: 'Política de Cookies',
    
    // Daily Briefing Placeholder
    readyForDailyBriefing: 'Listo para tu Resumen Diario',
    getAiPoweredSummary: 'Obtén un resumen impulsado por IA de TODAS tus tareas del día, incluyendo prioridades y tareas regulares',
    clickGetBriefingInstructions: 'Haz clic en "Obtener Resumen" para ver el texto, luego "Voz" para escucharlo',
    unableToGenerateBriefing: 'No se pudo generar tu resumen diario.',
    
    // Task Conflict Modal
    timeConflictCannotSave: 'Conflicto de Hora - No se Puede Guardar',
    schedulingConflictWarning: 'Advertencia de Conflicto de Programación',
    exactTimeConflictDetected: 'Conflicto de Hora Exacta Detectado',
    exactTimeConflictMessage: 'Ya tienes una tarea programada en la misma fecha y hora. Por favor cambia la hora para continuar.',
    nearbyTasksDetected: 'Tareas Cercanas Detectadas',
    nearbyTasksMessage: 'Tienes {count} tarea{plural} programada{plural} dentro de 2 horas de esta hora.',
    at: 'a las',
    editTime: 'Editar Hora',
    saveAnyway: 'Guardar de Todos Modos',
    
    // Calendar tooltips
    hasOverdueTasksTooltip: 'Tiene tareas vencidas',
    hasPendingTasksTooltip: 'Tiene tareas pendientes', 
    hasCompletedTasksTooltip: 'Tiene tareas completadas',
    
    // Missing translations
    addNewContact: 'Añadir nuevo contacto',
    noContactsFound: 'No se encontraron contactos',
    noContactsYet: 'Aún no hay contactos',
    noContactsFoundDesc: 'Ningún contacto coincide con',
    addFirstContact: '¡Añade tu primer contacto para empezar!',
    sendEmail: 'Enviar Email',
    
    // AddTaskModal
    createFollowUpTask: 'Crear Tarea de Seguimiento',
    addNewTask: 'Añadir Nueva Tarea',
    followUpTask: 'Tarea de Seguimiento',
    followUpTaskLinked: 'Esta tarea estará vinculada a la tarea padre completada.',
    title: 'Título',
    dueDate: 'Fecha de Vencimiento',
    time: 'Hora',
    category: 'Categoría',
    priority: 'Prioridad',
    location: 'Ubicación',
    contact: 'Contacto',
    notes: 'Notas',
    recurring: 'Recurrente',
    recurrencePattern: 'Patrón de Recurrencia',
    selectPattern: 'Seleccionar Patrón',
    daily: 'Diario',
    weekly: 'Semanal', 
    monthly: 'Mensual',
    yearly: 'Anual',
    endDate: 'Fecha de Fin',
    attachmentsMedia: 'Archivos Adjuntos y Multimedia',
    uploadFiles: 'Subir Archivos',
    recordAudio: 'Grabar Audio',
    takePhoto: 'Tomar Foto',
    recurringTask: 'Tarea Recurrente',
    uploading: 'Subiendo',
    createTask: 'Crear Tarea',
    creating: 'Creando...',
    creatingFollowUp: 'Creando Seguimiento...',
    recording: 'Grabando...',
    enterTaskTitle: 'Ingresa título de la tarea',
    addNotes: 'Añadir notas...',
    selectCategory: 'Seleccionar Categoría',
    selectContact: 'Seleccionar Contacto',
    today: 'Hoy',
    high: 'Alta',
    medium: 'Media', 
    low: 'Baja',
    enterLocation: 'Ingresa ubicación',
    month: 'Mes',
    week: 'Semana',
    taskStatusLegend: 'Leyenda del Estado de Tareas',
    pending: 'Pendiente',
    overdue: 'Vencida', 
    done: 'Hecho',
    january: 'Enero',
    february: 'Febrero',
    march: 'Marzo',
    april: 'Abril', 
    may: 'Mayo',
    june: 'Junio',
    july: 'Julio',
    august: 'Agosto',
    september: 'Septiembre',
    october: 'Octubre',
    november: 'Noviembre',
    december: 'Diciembre',
    editContact: 'Editar Contacto',
    
    // Auth
    login: 'Iniciar Sesión',
    register: 'Registrarse',
    signIn: 'Iniciar Sesión',
    signUp: 'Registrarse',
    forgotPassword: 'Olvidé mi Contraseña',
    resetPassword: 'Restablecer Contraseña',

    // Tasks list hints & actions
    loadAllTasksHint: 'Mostrando 20 — toca Todas las tareas para buscar y filtrar.',

    // Contacts page
    importFromPhone: 'Importar desde el teléfono',
    importingProgress: 'Importando…',
    addContactBtn: 'Añadir contacto',
    importTipText: 'Consejo: en tu teléfono, abre Contactos → Compartir/Exportar → elige vCard (.vcf) o CSV, luego súbelo aquí.',
    selectBtn: 'Seleccionar',
    selectAll: 'Seleccionar todo',
    clearBtn: 'Limpiar',
    deletingProgress: 'Eliminando…',
    selectedCountSuffix: 'seleccionado(s)',

    // Preferences / Notifications
    pushNotification: 'Notificación push',
    browserNotificationPermission: 'Las notificaciones del navegador necesitan tu permiso para funcionar.',
    enableNotifications: 'Activar notificaciones',
    enabledSendTest: '✅ Activadas - Enviar prueba',
    statusLabel: 'Estado:',
    statusEnabled: '✅ Activadas',
    statusBlocked: '❌ Bloqueadas',
    statusNotEnabled: '⚠️ No activadas',
    statusNotAvailablePreview: '⚠️ No disponible en vista previa',

    // Settings hubs
    preferencesHubSubtitle: 'Idioma, tema, recordatorios y resúmenes',
    categoriesHubSubtitle: 'Personaliza las categorías de tus tareas',

    // Administration
    administrationPanel: 'Panel de administración',
    systemUserControlsSubtitle: 'Gestión del sistema y controles de usuario',
    adminBtn: 'Admin',

    // Settings hub & sub-section subtitles
    settingsTitle: 'Configuración',
    settingsHubSubtitle: 'Gestiona tu cuenta',
    profileHubSubtitle: 'Nombre, correo y contraseña',
    subscriptionHubSubtitle: 'Plan, facturación y facturas',
    supportHubSubtitle: 'Centro de ayuda, comentarios, legal',
    pickASection: 'Elige una sección',
    chooseACategoryHelp: 'Elige una categoría a la izquierda para gestionar tu cuenta.',

    // Connected Clouds
    connectedClouds: 'Nubes conectadas',
    connectedCloudsHubSubtitle: 'Guarda los archivos de tus tareas en tu propio Google Drive',
    connectedCloudsTitle: 'Nubes conectadas',
    connectedCloudsSubtitle: 'Los archivos de tus tareas se guardan en tu propia nube. Solo almacenamos una referencia, nunca el archivo.',
    connectBtn: 'Conectar',
    disconnectBtn: 'Desconectar',
    cloudConnectedShort: 'Conectado',
    cloudNotConnected: 'No conectado',
    cloudConnectedBanner: 'Conectado correctamente.',
    cloudDisconnected: 'Nube desconectada.',
    cloudConnectFailed: 'Error al conectar. Inténtalo de nuevo.',
    cloudDisconnectFailed: 'No se pudo desconectar.',
    cloudDisconnectConfirm: '¿Desconectar esta nube? Los archivos adjuntos existentes seguirán funcionando, pero no podrás subir nuevos hasta volver a conectarla.',
    cloudReauthRequired: 'El acceso ha caducado — vuelve a conectar para seguir adjuntando archivos.',
    privacyLabel: 'Privacidad',
    cloudsPrivacyNote: 'Pedimos el permiso mínimo necesario: el ámbito drive.file de Google Drive — solo vemos los archivos creados por nuestra app. Los tokens se cifran en reposo.',

    // Sharing
    share: 'Compartir',
    shareThisTask: 'Compartir esta tarea',
    shareThisContact: 'Compartir este contacto',
    sendAsCalendarInvite: 'Enviar como invitación de calendario',
    sendAsCalendarInviteDesc: 'Se abre en Google, Outlook o Apple Calendar',
    copyShareLink: 'Copiar enlace para compartir',
    copyShareLinkDesc: 'Cualquiera con el enlace puede verlo, solo lectura',
    sendAsVCard: 'Enviar como vCard (.vcf)',
    sendAsVCardDesc: 'Se importa directamente en la app de Contactos del destinatario',
    sendAsPdfCard: 'Enviar como tarjeta PDF',
    sendAsPdfCardDesc: 'Un PDF de una página listo para adjuntar por correo',
    linkCopied: 'Enlace copiado al portapapeles',
    linkCopyFailed: 'No se pudo copiar el enlace, inténtalo de nuevo',
    sharedReadOnly: 'Compartido en solo lectura',
    openInEdotask: 'Abrir en Edotask',
    poweredByEdotask: 'Desarrollado por Edotask',
    sharedTaskExpired: 'Este enlace ya no es válido',
    sharedTaskExpiredDesc: 'El propietario lo revocó o la tarea fue eliminada.',


    // Misc strings
    weekDaySun: 'Dom',
    weekDayMon: 'Lun',
    weekDayTue: 'Mar',
    weekDayWed: 'Mié',
    weekDayThu: 'Jue',
    weekDayFri: 'Vie',
    weekDaySat: 'Sáb',
    youAreProUser: 'Eres un usuario Pro',
    privacyDataTitle: 'Privacidad y datos',

    // Account deletion
    dangerZone: 'Zona de peligro',
    dangerZoneSubtitle: 'Acciones irreversibles para esta cuenta.',
    deleteAccount: 'Eliminar cuenta',
    deleteAccountHint: 'Elimina permanentemente tu cuenta, tareas, contactos y ajustes.',
    deleteAccountTitle: 'Eliminar tu cuenta',
    deleteAccountBtn: 'Eliminar mi cuenta',
    deleteAccountProgress: 'Eliminando…',
    deleteAccountWarnTitle: 'Esta acción no se puede deshacer.',
    deleteAccountWarnBody: 'Todas tus tareas, contactos, archivos adjuntos, enlaces compartidos y ajustes se eliminarán de forma permanente. Se cancelará cualquier suscripción activa.',

    // Post-signup email verification banner (QA-001)

    // Resend verification email
    resendVerifyPrompt: '¿No lo has recibido?',
    resendVerifyCta: 'Reenviar correo',
    resendVerifySending: 'Enviando…',
    resendVerifySent: 'Enviado — revisa tu bandeja de entrada de nuevo.',
    resendVerifyCooldown: 'Enviado',
    resendVerifyError: 'No pudimos reenviarlo ahora. Inténtalo en un minuto.',

    signupCheckEmailTitle: 'Casi listo — revisa tu correo',
    signupCheckEmailBody: 'Se ha enviado un correo de confirmación. Abre tu bandeja de entrada (y la carpeta de Spam por si acaso) y haz clic en el enlace para activar tu cuenta.',

    deleteAccountPasswordLabel: 'Confirma tu contraseña',
    deleteAccountTypeDeleteLabel: 'Escribe DELETE para confirmar',

    privacyDataDesc: 'Gestiona lo que se almacena en este dispositivo. Tu cuenta en el servidor nunca se ve afectada.',
    micTip: 'Consejo: toca el micrófono de tu teclado para dictar en lugar de escribir.',
    appLastUpdated: 'Enero 2025',
    update: 'Actualizar',

    // Notifications
    notifications: 'Notificaciones',
    noNotifications: 'Estás al día.',
    teamInvitations: 'Invitaciones de equipo',
    dueSoon: 'Próximas',
    invitedAsRole: 'Invitado como',
    dueNow: 'Vence ahora',
    inLabel: 'en',
    minutesShort: 'min',
    newLabel: 'nuevas',

    // Organization invites
    inviteTeammate: 'Invitar a un compañero',
    inviteFreeCopy: 'Tu compañero se une gratis — tú cubres su asiento. Tu plan de $10/mes incluye a ti + 1 compañero; cada compañero adicional suma $5/mes cuando acepte.'
  },
  
  french: {
    // Navigation
    tasks: 'Tâches',
    contacts: 'Contacts',
    calendar: 'Calendrier',
    settings: 'Paramètres',
    
    // Common
    add: 'Ajouter',
    edit: 'Modifier',
    delete: 'Supprimer',
    save: 'Enregistrer',
    cancel: 'Annuler',
    loading: 'Chargement',
    search: 'Rechercher',
    filter: 'Filtrer',
    filters: 'Filtres',
    show: 'Afficher',
    hide: 'Masquer',
    clear: 'Effacer',
    clearSearch: 'Effacer la Recherche',
    filtered: 'filtrées',
    all: 'Tous',
    allCategories: 'Toutes les Catégories',
    completed: 'Terminées',
    pending: 'En attente',
    active: 'Actives',
    overdue: 'En retard',
    searchByTitle: 'Rechercher par titre',
    searchByNameOrPlace: 'Rechercher par nom ou lieu',
    noContactsFound: 'Aucun contact trouvé',
    noContactsFoundDesc: 'Aucun contact ne correspond à votre recherche',
    noContactsYet: 'Aucun contact pour le moment',
    addFirstContact: 'Ajoutez votre premier contact pour commencer !',
    sendEmail: 'Envoyer un Email',
    contactsExplanation: 'Liez des Contacts (personnes/entreprises) aux tâches. Consultez, modifiez ou supprimez à tout moment.',
    
    // AddTaskModal
    createFollowUpTask: 'Créer une Tâche de Suivi',
    addNewTask: 'Ajouter une Nouvelle Tâche',
    followUpTask: 'Tâche de Suivi',
    followUpTaskLinked: 'Cette tâche sera liée à la tâche parent terminée.',
    title: 'Titre',
    dueDate: 'Date d\'Échéance',
    time: 'Heure',
    category: 'Catégorie',
    priority: 'Priorité',
    location: 'Emplacement',
    contact: 'Contact',
    notes: 'Notes',
    recurring: 'Récurrent',
    recurrencePattern: 'Modèle de Récurrence',
    selectPattern: 'Sélectionner un Modèle',
    daily: 'Quotidien',
    weekly: 'Hebdomadaire', 
    monthly: 'Mensuel',
    yearly: 'Annuel',
    endDate: 'Date de Fin',
    attachmentsMedia: 'Pièces Jointes et Médias',
    uploadFiles: 'Télécharger des Fichiers',
    recordAudio: 'Enregistrer l\'Audio',
    takePhoto: 'Prendre une Photo',
    recurringTask: 'Tâche Récurrente',
    uploading: 'Téléchargement',
    createTask: 'Créer une Tâche',
    creating: 'Création...',
    creatingFollowUp: 'Création du Suivi...',
    recording: 'Enregistrement...',
    enterTaskTitle: 'Entrez le titre de la tâche',
    addNotes: 'Ajouter des notes...',
    selectCategory: 'Sélectionner une Catégorie',
    selectContact: 'Sélectionner un Contact',
    today: 'Aujourd\'hui',
    high: 'Élevée',
    medium: 'Moyenne', 
    low: 'Faible',
    enterLocation: 'Entrez l\'emplacement',
    month: 'Mois',
    week: 'Semaine',
    taskStatusLegend: 'Légende du Statut des Tâches',
    pending: 'En Attente',
    overdue: 'En Retard', 
    done: 'Terminé',
    january: 'Janvier',
    february: 'Février',
    march: 'Mars',
    april: 'Avril', 
    may: 'Mai',
    june: 'Juin',
    july: 'Juillet',
    august: 'Août',
    september: 'Septembre',
    october: 'Octobre',
    november: 'Novembre',
    december: 'Décembre',
    editContact: 'Edit Contact',
    
    // Tasks
    addTask: 'Ajouter une Tâche',
    editTask: 'Modifier la Tâche',
    updateTask: 'Mettre à Jour la Tâche',
    updating: 'Mise à jour...',
    taskTitle: 'Titre de la Tâche',
    taskDetails: 'Détails',
    dueDate: 'Date d\'échéance',
    time: 'Heure',
    category: 'Catégorie',
    priority: 'Priorité',
    location: 'Lieu',
    contact: 'Contact',
    notes: 'Notes',
    markComplete: 'Marquer Terminée',
    createFollowUpTask: 'Créer une Tâche de Suivi',
    enterLocation: 'Entrer le lieu',
    currentAttachments: 'Pièces Jointes Actuelles',
    addNewAttachments: 'Ajouter de Nouvelles Pièces Jointes',
    loadingCategories: 'Chargement des catégories...',
    
    // Priority levels
    low: 'Faible',
    medium: 'Moyenne',
    high: 'Élevée',
    
    // Contacts
    addContact: 'Ajouter un Contact',
    editContact: 'Modifier le Contact',
    contactDetails: 'Détails',
    contactName: 'Nom du Contact',
    contactNotFound: 'Contact Non Trouvé',
    contactNotFoundDesc: 'Ce contact n\'existe pas ou a été supprimé.',
    goBack: 'Retour',
    email: 'Email',
    phone: 'Téléphone',
    company: 'Entreprise',
    
    // Settings
    profile: 'Profil',
    preferences: 'Préférences',
    categories: 'Catégories',
    subscription: 'Abonnement',
    support: 'Support',
    
    // Preferences
    pushNotifications: 'Notifications Push',
    weeklyDigest: 'Résumé Hebdomadaire par Email',
    weeklyDigestDesc: 'Tâches en retard de la semaine passée + tâches à venir',
    dailyDigest: 'Résumé Quotidien par Email',
    dailyDigestDesc: 'En retard aujourd\'hui + toutes les tâches de demain',
    dailyDigestHint: 'Envoyé chaque jour à cette heure',
    dailyBriefPush: 'Notification Quotidienne Push',
    dailyBriefPushDesc: 'Résumé quotidien: Tâches du jour, en retard d\'hier, haute priorité de demain',
    dailyBriefPushHint: 'Envoyé une fois par jour à cette heure',
    taskReminders: 'Rappels de Tâches (Push)',
    taskRemindersDesc: '1 heure avant l\'échéance, filtré par priorité',
    taskRemindersHint: 'Notifications envoyées 1 heure avant l\'échéance',
    // Task reminder method picker (added 2026-02-22 to fix missing FR strings)
    reminderMethod: 'Méthode de rappel',
    email: 'E-mail',
    sms: 'SMS',
    usOnly: '(États-Unis uniquement)',
    phoneNumber: 'Numéro de téléphone',
    usPhoneFormat: 'Format États-Unis : +1XXXXXXXXXX ou XXXXXXXXXX',
    intlPhoneFormatHint: 'Incluez l\'indicatif pays (par ex. +1 pour les USA, +33 pour la France). Un numéro à 10 chiffres sans indicatif est traité comme américain.',
    reminderInfo: 'Pour recevoir des rappels, cochez la case « Rappel » lors de la création ou de la modification d\'une tâche. Les rappels sont envoyés 1 heure avant l\'échéance.',
    enableReminder: 'Activer le rappel',
    reminderDescription: 'Recevez un rappel 1 heure avant l\'échéance par le moyen de votre choix (e-mail ou SMS)',
    reminderPriorities: 'Envoyer des rappels pour:',
    priority: 'Priorité',
    darkMode: 'Mode Sombre',
    language: 'Langue',
    notificationDesc: 'Recevoir des rappels et mises à jour de tâches',
    darkModeDesc: 'Passer au thème sombre',
    digestDay: 'Jour',
    digestTime: 'Heure',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
    
    // Profile
    basicInformation: 'Informations de Base',
    name: 'Nom',
    changePassword: 'Changer le Mot de Passe',
    currentPassword: 'Mot de Passe Actuel',
    newPassword: 'Nouveau Mot de Passe',
    confirmPassword: 'Confirmer le Mot de Passe',
    
    // Categories
    myCategories: 'Mes Catégories',
    addCategory: 'Ajouter une Catégorie',
    editCategory: 'Modifier la Catégorie',
    categoryName: 'Nom de la Catégorie',
    color: 'Couleur',
    noCategoriesYet: 'Aucune catégorie créée pour le moment',
    createCategoriesToOrganize: 'Créez des catégories pour organiser vos tâches',
    createCategoriesDesc: 'Créez des catégories pour organiser vos tâches',
    tips: 'Conseils',
    categoriesTip1: 'Les catégories aident à organiser vos tâches par type ou projet',
    categoriesTip2: 'Choisissez des couleurs qui ont du sens pour chaque catégorie',
    categoriesTip3: 'Les catégories apparaîtront dans tous les formulaires de création et d\'édition de tâches',
    categoriesTip4: 'Vous pouvez filtrer les tâches par catégorie sur la page principale des Tâches',
    
    // Messages
    taskCompleted: 'Tâche terminée avec succès !',
    taskDeleted: 'Tâche supprimée avec succès !',
    contactSaved: 'Contact enregistré avec succès !',
    passwordChanged: 'Mot de passe changé avec succès !',
    settingsSaved: 'Paramètres enregistrés avec succès !',
    
    // Common UI
    selectCategory: 'Sélectionner une Catégorie',
    selectContact: 'Sélectionner un Contact',
    today: 'Aujourd\'hui',
    thisWeek: 'Cette Semaine',
    next7Days: 'Prochains 7 Jours',
    overdue: 'En retard',
    general: 'Général',
    administration: 'Administration',
    dailyBriefing: 'Point Quotidien',
    // Business workspaces — task visibility
    visibility: 'Visibilité',
    visibilityTeam: 'Équipe',
    visibilityPrivate: 'Privée (moi seul)',
    visibilityTeamHint: 'Visible par tous les membres de cet espace',
    visibilityPrivateHint: 'Seul vous pouvez voir cette tâche',
    getBriefing: 'Le Point',
    voiceButton: 'Voix',
    activeTasks: 'Tâches Actives',
    completedTasks: 'Tâches Terminées',
    archives: 'Archives',
    archivedTasks: 'Tâches Archivées',
    allArchives: 'Toutes les Archives',
    archive: 'Archiver',
    restore: 'Restaurer',
    archiveTask: 'Archiver la Tâche',
    restoreTask: 'Restaurer la Tâche',
    taskArchivedSuccessfully: 'Tâche archivée avec succès',
    taskRestoredSuccessfully: 'Tâche restaurée avec succès',
    noArchivedTasks: 'Aucune tâche archivée',
    searchByTitle: 'Rechercher par titre...',
    filters: 'Filtres',
    allPriorities: 'Toutes les Priorités',
    fromDate: 'Date de Début',
    toDate: 'Date de Fin',
    clearAllFilters: 'Effacer Tous les Filtres',
    selectTasks: 'Sélectionner des Tâches',
    loadAllArchives: 'Charger toutes les archives pour accéder à la recherche et aux filtres',
    showingFirstTwentyTasks: 'Affichage des 20 premières tâches archivées',
    loadMoreTasks: 'Charger Plus de Tâches',
    more: 'plus',
    createTask: 'Créer une Tâche',
    quickActions: 'Actions Rapides',
    sendEmail: 'Envoyer un Email',
    call: 'Appeler',
    allContacts: 'Tous les Contacts',
    signOut: 'Se Déconnecter',
    signOutConfirm: 'Êtes-vous sûr de vouloir vous déconnecter ?',
    noTasksFound: 'Aucune tâche trouvée',
    noTasksAssigned: 'Aucune tâche assignée à ce contact',
    loadingTasks: 'Chargement des tâches...',
    showingFirstTasks: 'Affichage des premières',
    loadAllTasks: 'Charger toutes les tâches pour accéder à la recherche et aux filtres',
    addNotes: 'Ajouter des notes...',
    completionNotes: 'Notes d\'achèvement',
    // Missing UI elements
    loadAllTasks: 'Voir Toutes',
    aiPoweredTaskSummary: 'Résumé de tâches alimenté par IA',
    generateBriefing: 'Générez votre résumé quotidien',
    goodMorning: 'Bonjour',
    goodAfternoon: 'Bon après-midi',
    goodEvening: 'Bonsoir',
    nothingScheduled: 'Rien de programmé',
    upcomingTasks: 'Tâches à venir',
    contactDetails: 'Détails',
    personalInfo: 'Informations Personnelles',
    accountSecurity: 'Compte et Sécurité',
    systemPreferences: 'Préférences Système',
    helpSupport: 'Aide et Support',
    contentManagement: 'Gestion de Contenu',
    userManagement: 'Gestion des Utilisateurs',
    systemConfiguration: 'Configuration Système',
    basicInfo: 'Informations de Base',
    updateProfile: 'Mettre à Jour le Profil',
    passwordSecurity: 'Mot de Passe et Sécurité',
    notificationSettings: 'Paramètres de Notification',
    languageRegion: 'Langue et Région',
    voiceTaskCreator: 'Créateur de Tâches Vocales',
    speakTaskDetails: 'Parlez naturellement des détails de votre tâche',
    aiWillHelp: 'L\'IA vous aidera à compléter les détails',
    
    // AI Assistant phrases
    listeningPleaseAsk: 'J\'écoute. Veuillez poser votre question maintenant.',
    pleaseAskQuestion: 'Veuillez me poser une question sur Edotask ou la gestion des tâches.',
    voiceRecognitionError: 'Erreur de reconnaissance vocale. Veuillez essayer de taper votre question.',
    iHeardLetMeHelp: 'J\'ai entendu votre question. Laissez-moi vous aider avec cela.',
    
    // AI Task Creator
    aiTaskCreator: 'Créateur de Tâches IA',
    readyToHelp: 'Prêt à Aider',
    aiHelpCreateTask: 'Je vous aiderai à créer une tâche en utilisant la saisie vocale ou textuelle.',
    voiceFeedbackQuality: 'La qualité des retours vocaux dépend du support de synthèse vocale de votre navigateur',
    startVoice: 'Démarrer la Voix',
    text: 'Texte',
    tryExample: 'Essayez : "Réunion avec Jean demain à 14h" ou utilisez la saisie textuelle',
    
    // AI Task Creator Voice Responses
    welcomeMessage: 'Veuillez me dire le titre de la tâche, la date et l\'heure. J\'assignerai une catégorie si vous n\'en mentionnez pas, je définirai la priorité comme moyenne, et vous pourrez la modifier plus tard.',
    letMeProcess: 'Laissez-moi traiter cela pour vous...',
    processingResponse: 'Compris, laissez-moi traiter cela...',
    createTaskNow: 'Création de votre tâche maintenant...',
    taskCreatedSuccess: 'Tâche créée avec succès !',
    taskCreationFailed: 'Désolé, je n\'ai pas pu créer la tâche. Veuillez réessayer.',
    needTitleAtLeast: 'J\'aimerais créer la tâche, mais j\'ai besoin d\'au moins un titre. Comment devrais-je appeler cette tâche ?',
    didntCatchContactName: 'Je n\'ai pas saisi le nom du contact. Pourriez-vous dire quelque chose comme "Ai-je un contact nommé Jean Wilson ?"',
    troubleCheckingContacts: 'J\'ai eu des difficultés à vérifier vos contacts. Continuons avec la création de la tâche.',
    askingAboutTaskDetails: 'Je demandais des détails sur votre tâche. Continuons où nous nous sommes arrêtés.',
    haveEverythingReady: 'J\'ai tout ce dont j\'ai besoin ! Prêt à créer votre tâche.',
    describeTaskNaturally: 'Décrivez simplement votre tâche naturellement. Je demanderai tous les détails manquants.',
    perfectEnoughInfo: 'Parfait ! J\'ai suffisamment d\'informations. Laissez-moi créer votre tâche.',
    whenIsTaskDue: 'Quand "{taskTitle}" est-elle due ?',
    creatingTaskWithInfo: 'Excellent ! Je crée votre tâche avec les informations que j\'ai.',
    whatToChange: 'Que souhaitez-vous changer ? Vous pouvez dire des choses comme "changer la date", "mettre à jour la priorité", ou "catégorie différente".',
    startFreshTask: 'Pas de problème ! Repartons à zéro. Quelle tâche souhaitez-vous créer ?',
    perfectReadyToCreate: 'Parfait ! Prêt à créer votre tâche.',
    notSureWhatField: 'Je ne suis pas sûr du champ auquel vous faites référence. Pourriez-vous être plus précis ?',
    didntCatchRepeat: 'Je n\'ai pas bien saisi cela. Pourriez-vous répéter cette information s\'il vous plaît ?',
    couldntUnderstandTask: 'Je n\'ai pas pu comprendre cela. Pourriez-vous décrire votre tâche à nouveau s\'il vous plaît ?',
    voiceNotSupported: 'La reconnaissance vocale n\'est pas prise en charge dans votre navigateur. Veuillez utiliser l\'option de saisie textuelle.',
    needsHTTPS: 'La reconnaissance vocale nécessite une connexion sécurisée. Veuillez utiliser HTTPS ou essayer l\'option de saisie textuelle.',
    microphoneBlocked: 'L\'accès au microphone est bloqué. Veuillez autoriser les permissions du microphone dans les paramètres de votre navigateur et réessayer, ou utiliser l\'option de saisie textuelle.',
    voiceCouldntStart: 'La reconnaissance vocale n\'a pas pu démarrer. Veuillez vérifier les permissions de votre microphone et réessayer, ou utiliser l\'option de saisie textuelle.',
    listeningStopped: 'Écoute arrêtée. Vous pouvez recommencer ou utiliser la saisie textuelle.',
    
    // AI Task Creator Follow-up Questions
    questionWhenDue: 'Quand "{taskTitle}" est-elle due ?',
    questionWhatTime: 'À quelle heure pour "{taskTitle}" ?',
    questionPriority: 'Priorité pour "{taskTitle}" ? Élevée, moyenne ou faible ?',
    questionLocation: 'Où aura lieu "{taskTitle}" ?',
    questionCategory: 'Quelle catégorie pour "{taskTitle}" ? Travail, personnel, santé, éducation, achats, famille, finances ou voyage ?',
    questionContact: '"{taskTitle}" devrait-elle être liée à un contact ?',
    questionNotes: 'Des notes pour "{taskTitle}" ?',
    questionDescription: 'Plus de détails sur "{taskTitle}" ?',
    questionGeneric: 'Qu\'en est-il de {field} pour "{taskTitle}" ?',
    thisTask: 'cette tâche',
    
    // AI Task Creator Dynamic Responses
    iAsked: 'J\'ai demandé : {question}',
    iStillNeed: 'J\'ai encore besoin de : {remaining}. Commençons par {nextField}.',
    okaySkipping: 'D\'accord, on passe ça. {nextQuestion}',
    greatUsing: 'Parfait ! Utilisant {contactName}. {nextQuestion}',
    okayLetMeAskAgain: 'D\'accord, laissez-moi demander à nouveau. {question}',
    didntCatchInfoClearly: 'Je n\'ai pas bien saisi cette information. Laissez-moi redemander à propos de {fieldName}.',
    
    // Contact Search Responses
    foundContactConfirm: 'Oui, j\'ai trouvé {contactName} dans vos contacts. Souhaitez-vous lier cette tâche à {contactName} ?',
    voiceContactNotFound: 'Je ne vois pas de contact nommé {contactName} dans vos contacts. Souhaitez-vous que je continue sans contact, ou voulez-vous spécifier un nom différent ?',
    
    // Task Creation Confirmation
    perfectCreateTaskWith: 'Parfait ! Je vais créer votre tâche avec les informations que j\'ai : {taskTitle}',
    
    // Schema and Options Responses
    availableCategories: 'Les catégories disponibles sont : {categories}.',
    priorityLevels: 'Les niveaux de priorité sont : {priorities}.',
    
    // Contact Rejection Patterns  
    noContactNeeded: 'Compris, aucun contact nécessaire. {nextAction}',
    continueWithoutContact: 'Continuation sans contact. {nextAction}',
    
    // Field Update Confirmations
    perfectSetField: 'Parfait ! J\'ai défini {fieldName} à {value}.',
    excellentGotBoth: 'Excellent ! J\'ai {field1} et {field2}.',
    greatSetMultiple: 'Parfait ! J\'ai défini {fields}, et {lastField}.',
    
    // Field Help Responses
    fieldExamples: 'Pour {fieldDescription}, vous pouvez dire : {examples}.',
    fieldOptions: 'Pour {fieldDescription}, choisissez : {options}.',
    fieldInstructions: 'Dites-moi simplement {fieldDescription}.',
    
    // AI Task Creator Progress Modal
    needMoreInfo: 'Besoin de Plus d\'Informations',
    pleaseProvideRequested: 'Veuillez fournir les informations demandées',
    taskProgress: 'Progrès de la tâche :',
    stillNeedFields: 'Encore besoin de : {fields}',
    continueAnswering: 'Continuer à Répondre',
    
    // Field Names Translation
    fieldNames: {
      title: 'titre',
      due_date: 'date d\'échéance',
      due_time: 'heure d\'échéance',
      time: 'heure',
      priority: 'priorité', 
      category: 'catégorie',
      location: 'lieu',
      contact: 'contact',
      notes: 'notes',
      description: 'description'
    },
    
    // Daily Briefing phrases
    goodMorningBriefing: 'Bonjour ! Voici votre résumé quotidien',
    goodAfternoonBriefing: 'Bon après-midi ! Voici votre résumé quotidien',
    goodEveningBriefing: 'Bonsoir ! Voici votre résumé quotidien',
    
    // Task List Elements (Home Page)
    showingFirstTwentyTasks: 'Affichage des 20 premières tâches',
    followUpOf: 'Suivi de',
    recurring: 'Récurrent',
    instance: 'Instance',
    followUps: 'suivis',
    filtered: '(Filtré)',
    parentTask: 'Tâche Parent',
    viewFollowUpTasks: 'Voir les tâches de suivi',
    clickToFilterRecurring: 'Cliquez pour filtrer le groupe récurrent',
    showAllTasks: 'afficher toutes les tâches',
    
    // Task Details Page
    taskNotFound: 'Tâche Non Trouvée',
    taskNotFoundDesc: 'Cette tâche n\'existe pas ou a été supprimée.',
    priorityLabel: 'Priorité',
    completedLabel: 'Terminée',
    jumpToRecurringSection: 'Aller à la section des tâches récurrentes ci-dessous',
    completionInfo: 'Cliquez pour compléter. Ajoutez fichier/note si nécessaire. Les tâches terminées ne peuvent pas être modifiées, seulement supprimées.',
    followUpTaskInfo: 'Créez des tâches connexes ou les prochaines étapes pour garder le travail connecté.',
    thisTaskHasFollowUps: 'Cette tâche a des tâches de suivi',
    
    // Calendar Page
    monthNames: {
      january: 'Janvier',
      february: 'Février', 
      march: 'Mars',
      april: 'Avril',
      may: 'Mai',
      june: 'Juin',
      july: 'Juillet',
      august: 'Août',
      september: 'Septembre',
      october: 'Octobre',
      november: 'Novembre',
      december: 'Décembre'
    },
    weekDays: {
      sun: 'Dim',
      mon: 'Lun', 
      tue: 'Mar',
      wed: 'Mer',
      thu: 'Jeu',
      fri: 'Ven',
      sat: 'Sam'
    },
    legend: 'Légende',
    hasPendingTasks: 'A des Tâches En Attente',
    hasOverdueTasks: 'A des Tâches En Retard', 
    hasCompletedTasks: 'A des Tâches Terminées',
    tasksFor: 'Tâches pour',
    noTasksForDate: 'Aucune tâche pour cette date',
    loadingCalendar: 'Chargement du Calendrier',
    preparingTasks: 'Préparation de vos tâches...',
    loadingYourCalendar: 'Chargement de votre calendrier...',
    
    // Contacts
    searchContacts: 'Rechercher des contacts...',
    loadingContacts: 'Chargement des contacts...',
    contactInformation: 'Informations du Contact',
    place: 'Lieu',
    address: 'Adresse',
    clickToOpenMaps: 'Cliquez pour ouvrir dans Google Maps',
    enterContactName: 'Entrez le nom du contact',
    contactEmailPlaceholder: 'contact@exemple.com',
    phonePlaceholder: '+33 1 23 45 67 89',
    placeName: 'Nom du lieu',
    enterFullAddress: 'Entrez l\'adresse complète',
    createContact: 'Créer un Contact',
    
    // Settings Page
    manageYourAccount: 'Gérer votre compte',
    signOut: 'Se Déconnecter',
    areYouSureSignOut: 'Êtes-vous sûr de vouloir vous déconnecter ?',
    
    // Profile Tab
    basicInformation: 'Informations de Base',
    updateProfile: 'Mettre à Jour le Profil',
    password: 'Mot de Passe',
    cancel: 'Annuler',
    change: 'Changer',
    currentPassword: 'Mot de Passe Actuel',
    newPassword: 'Nouveau Mot de Passe',
    confirmNewPassword: 'Confirmer le Nouveau Mot de Passe',
    changePassword: 'Changer le Mot de Passe',
    profileUpdatedSuccessfully: 'Profil mis à jour avec succès !',
    passwordsDoNotMatch: 'Les nouveaux mots de passe ne correspondent pas',
    passwordChangedSuccessfully: 'Mot de passe changé avec succès !',
    
    // Preferences Tab  
    preferences: 'Préférences',
    pushNotifications: 'Notifications Push',
    receiveTaskReminders: 'Recevoir des rappels et mises à jour de tâches',
    emailDigest: 'Résumé par Email',
    weeklySummaryTasks: 'Résumé hebdomadaire de vos tâches',
    darkMode: 'Mode Sombre',
    switchToDarkTheme: 'Passer au thème sombre',
    
    // Categories Tab
    addNewCategory: 'Ajouter une Nouvelle Catégorie',
    editCategory: 'Modifier la Catégorie',
    categoryName: 'Nom de la Catégorie',
    enterCategoryName: 'Entrez le nom de la catégorie',
    color: 'Couleur',
    addCategory: 'Ajouter une Catégorie',
    updateCategory: 'Mettre à Jour la Catégorie',
    myCategories: 'Mes Catégories',
    noCategoriesCreated: 'Aucune catégorie créée pour le moment',
    createCategoriesToOrganize: 'Créez des catégories pour organiser vos tâches',
    deleteCategoryConfirm: 'Êtes-vous sûr de vouloir supprimer la catégorie',
    failedToSaveCategory: 'Échec de la sauvegarde de la catégorie. Veuillez réessayer.',
    failedToDeleteCategory: 'Échec de la suppression de la catégorie. Veuillez réessayer.',
    
    // Subscription Tab
    currentPlan: 'Plan Actuel',
    plan: 'Plan',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    upgradeToPro: 'Passer à Pro',
    usageStatistics: 'Statistiques d\'Utilisation',
    tasksCreated: 'Tâches Créées',
    storageUsed: 'Stockage Utilisé',
    freePlan: 'Plan Gratuit',
    statusActive: 'Statut : Actif',
    billingHistory: 'Historique de Facturation',
    noBillingHistory: 'Aucun historique de facturation disponible',
    upgradeToSeeBilling: 'Passez à Pro pour voir les informations de facturation',
    
    // Support Tab
    helpSupport: 'Aide et Support',
    helpCenter: 'Centre d\'Aide',
    browseFrequentlyAsked: 'Parcourir les questions fréquemment posées',
    contactSupport: 'Contacter le Support',
    sendFeedback: 'Envoyer des Commentaires',
    helpUsImprove: 'Aidez-nous à améliorer Edotask',
    appInformation: 'Informations de l\'App',
    version: 'Version',
    lastUpdated: 'Dernière Mise à Jour',
    allSystemsOperational: 'Tous les Systèmes Opérationnels',
    aiHelpAssistant: 'Assistant d\'Aide IA',
    askMeAnything: 'Demandez-moi n\'importe quoi sur Edotask',
    voiceEnabled: 'VOIX ACTIVÉE',
    legal: 'Légal',
    privacyPolicy: 'Politique de Confidentialité',
    termsOfService: 'Conditions d\'Utilisation',
    cookiePolicy: 'Politique des Cookies',
    
    // Daily Briefing Placeholder
    readyForDailyBriefing: 'Prêt pour Votre Résumé Quotidien',
    getAiPoweredSummary: 'Obtenez un résumé alimenté par IA de TOUTES vos tâches du jour, y compris les priorités et tâches régulières',
    clickGetBriefingInstructions: 'Cliquez sur "Obtenir le Résumé" pour voir le texte, puis "Voix" pour l\'entendre',
    unableToGenerateBriefing: 'Impossible de générer votre résumé quotidien.',
    
    // Task Conflict Modal
    timeConflictCannotSave: 'Conflit d\'Heure - Impossible d\'Enregistrer',
    schedulingConflictWarning: 'Avertissement de Conflit de Planification',
    exactTimeConflictDetected: 'Conflit d\'Heure Exacte Détecté',
    exactTimeConflictMessage: 'Vous avez déjà une tâche programmée à la même date et heure. Veuillez modifier l\'heure pour continuer.',
    nearbyTasksDetected: 'Tâches Proches Détectées',
    nearbyTasksMessage: 'Vous avez {count} tâche{plural} programmée{plural} dans les 2 heures de cette heure.',
    at: 'à',
    editTime: 'Modifier l\'Heure',
    saveAnyway: 'Enregistrer Quand Même',
    
    // Calendar tooltips
    hasOverdueTasksTooltip: 'A des tâches en retard',
    hasPendingTasksTooltip: 'A des tâches en attente', 
    hasCompletedTasksTooltip: 'A des tâches terminées',
    
    // Missing translations
    addNewContact: 'Ajouter un nouveau contact',
    noContactsFound: 'Aucun contact trouvé',
    noContactsYet: 'Pas encore de contacts',
    noContactsFoundDesc: 'Aucun contact ne correspond à',
    addFirstContact: 'Ajoutez votre premier contact pour commencer !',
    
    // Auth
    login: 'Connexion',
    register: 'S\'inscrire',
    signIn: 'Se Connecter',
    signUp: 'S\'inscrire',
    forgotPassword: 'Mot de Passe Oublié',
    resetPassword: 'Réinitialiser le Mot de Passe',

    // Tasks list hints & actions
    loadAllTasksHint: 'Affichage de 20 — appuyez sur Toutes les tâches pour rechercher et filtrer.',

    // Contacts page
    importFromPhone: 'Importer depuis le téléphone',
    importingProgress: 'Importation…',
    addContactBtn: 'Ajouter un contact',
    importTipText: 'Astuce : sur votre téléphone, ouvrez Contacts → Partager/Exporter → choisissez vCard (.vcf) ou CSV, puis téléchargez-le ici.',
    selectBtn: 'Sélectionner',
    selectAll: 'Tout sélectionner',
    clearBtn: 'Effacer',
    deletingProgress: 'Suppression…',
    selectedCountSuffix: 'sélectionné(s)',

    // Preferences / Notifications
    pushNotification: 'Notification push',
    browserNotificationPermission: 'Les notifications du navigateur nécessitent votre autorisation pour fonctionner.',
    enableNotifications: 'Activer les notifications',
    enabledSendTest: '✅ Activées - Envoyer un test',
    statusLabel: 'Statut :',
    statusEnabled: '✅ Activées',
    statusBlocked: '❌ Bloquées',
    statusNotEnabled: '⚠️ Non activées',
    statusNotAvailablePreview: '⚠️ Indisponible en aperçu',

    // Settings hubs
    preferencesHubSubtitle: 'Langue, thème, rappels et résumés',
    categoriesHubSubtitle: 'Personnalisez vos catégories de tâches',

    // Administration
    administrationPanel: 'Panneau d\'administration',
    systemUserControlsSubtitle: 'Gestion du système et contrôles utilisateurs',
    adminBtn: 'Admin',

    // Settings hub & sub-section subtitles
    settingsTitle: 'Paramètres',
    settingsHubSubtitle: 'Gérez votre compte',
    profileHubSubtitle: 'Nom, e-mail et mot de passe',
    subscriptionHubSubtitle: 'Forfait, facturation et factures',
    supportHubSubtitle: 'Centre d\'aide, commentaires, mentions légales',
    pickASection: 'Choisissez une section',
    chooseACategoryHelp: 'Choisissez une catégorie à gauche pour gérer votre compte.',

    // Connected Clouds
    connectedClouds: 'Clouds connectés',
    connectedCloudsHubSubtitle: 'Stockez les fichiers des tâches dans votre propre Google Drive',
    connectedCloudsTitle: 'Clouds connectés',
    connectedCloudsSubtitle: 'Les fichiers de vos tâches sont stockés dans votre propre cloud. Nous ne conservons qu\'une référence, jamais le fichier.',
    connectBtn: 'Connecter',
    disconnectBtn: 'Déconnecter',
    cloudConnectedShort: 'Connecté',
    cloudNotConnected: 'Non connecté',
    cloudConnectedBanner: 'Connecté avec succès.',
    cloudDisconnected: 'Cloud déconnecté.',
    cloudConnectFailed: 'Échec de la connexion. Veuillez réessayer.',
    cloudDisconnectFailed: 'Impossible de déconnecter.',
    cloudDisconnectConfirm: 'Déconnecter ce cloud ? Les pièces jointes existantes continueront de fonctionner, mais vous ne pourrez plus en téléverser tant que vous ne vous reconnectez pas.',
    cloudReauthRequired: 'Accès expiré — reconnectez-vous pour continuer à joindre des fichiers.',
    privacyLabel: 'Confidentialité',
    cloudsPrivacyNote: 'Nous demandons la permission minimale nécessaire : le périmètre drive.file de Google Drive — nous ne voyons que les fichiers créés par notre application. Les jetons sont chiffrés au repos.',

    // Sharing
    share: 'Partager',
    shareThisTask: 'Partager cette tâche',
    shareThisContact: 'Partager ce contact',
    sendAsCalendarInvite: 'Envoyer comme invitation calendrier',
    sendAsCalendarInviteDesc: 'S\'ouvre dans Google, Outlook ou Apple Calendar',
    copyShareLink: 'Copier le lien de partage',
    copyShareLinkDesc: 'Toute personne ayant le lien peut voir, en lecture seule',
    sendAsVCard: 'Envoyer comme vCard (.vcf)',
    sendAsVCardDesc: 'S\'importe directement dans l\'app Contacts du destinataire',
    sendAsPdfCard: 'Envoyer comme carte PDF',
    sendAsPdfCardDesc: 'Un PDF d\'une page prêt à joindre par e-mail',
    linkCopied: 'Lien copié dans le presse-papiers',
    linkCopyFailed: 'Impossible de copier le lien, veuillez réessayer',
    sharedReadOnly: 'Partagé en lecture seule',
    openInEdotask: 'Ouvrir dans Edotask',
    poweredByEdotask: 'Propulsé par Edotask',
    sharedTaskExpired: 'Ce lien de partage n\'est plus valide',
    sharedTaskExpiredDesc: 'Le propriétaire l\'a révoqué ou la tâche a été supprimée.',


    // Misc strings
    weekDaySun: 'Dim',
    weekDayMon: 'Lun',
    weekDayTue: 'Mar',
    weekDayWed: 'Mer',
    weekDayThu: 'Jeu',
    weekDayFri: 'Ven',
    weekDaySat: 'Sam',
    youAreProUser: 'Vous êtes un utilisateur Pro',
    privacyDataTitle: 'Confidentialité et données',

    // Account deletion
    dangerZone: 'Zone de danger',
    dangerZoneSubtitle: 'Actions irréversibles pour ce compte.',
    deleteAccount: 'Supprimer le compte',
    deleteAccountHint: 'Supprime définitivement votre compte, tâches, contacts et paramètres.',
    deleteAccountTitle: 'Supprimer votre compte',
    deleteAccountBtn: 'Supprimer mon compte',
    deleteAccountProgress: 'Suppression…',
    deleteAccountWarnTitle: 'Cette action est irréversible.',

    // Resend verification email
    resendVerifyPrompt: 'Vous ne l\'avez pas reçu ?',
    resendVerifyCta: 'Renvoyer l\'e-mail',
    resendVerifySending: 'Envoi…',
    resendVerifySent: 'Envoyé — vérifiez à nouveau votre boîte de réception.',
    resendVerifyCooldown: 'Envoyé',
    resendVerifyError: 'Impossible de renvoyer pour le moment. Réessayez dans une minute.',


    // Post-signup email verification banner (QA-001)
    signupCheckEmailTitle: 'Presque terminé — vérifiez votre e-mail',
    signupCheckEmailBody: 'Un e-mail de confirmation a été envoyé. Ouvrez votre boîte de réception (et le dossier Spam au cas où) et cliquez sur le lien pour activer votre compte.',

    deleteAccountWarnBody: 'Toutes vos tâches, contacts, pièces jointes, liens de partage et paramètres seront définitivement supprimés. Tout abonnement actif sera annulé.',
    deleteAccountPasswordLabel: 'Confirmez votre mot de passe',
    deleteAccountTypeDeleteLabel: 'Tapez DELETE pour confirmer',

    privacyDataDesc: 'Gérez ce qui est stocké sur cet appareil. Votre compte sur le serveur n\'est jamais affecté.',
    micTip: 'Astuce : appuyez sur le micro de votre clavier pour dicter au lieu de taper.',
    appLastUpdated: 'Janvier 2025',
    update: 'Mettre à jour',

    // Notifications
    notifications: 'Notifications',
    noNotifications: 'Vous êtes à jour.',
    teamInvitations: 'Invitations d\'équipe',
    dueSoon: 'À venir',
    invitedAsRole: 'Invité en tant que',
    dueNow: 'Dû maintenant',
    inLabel: 'dans',
    minutesShort: 'min',
    newLabel: 'nouvelles',

    // Organization invites
    inviteTeammate: 'Inviter un coéquipier',
    inviteFreeCopy: 'Votre coéquipier rejoint gratuitement — vous couvrez son siège. Votre forfait à 10 $/mois inclut vous + 1 coéquipier ; chaque coéquipier supplémentaire ajoute 5 $/mois dès qu\'il accepte.'
  },
  
  german: {
    // Navigation
    tasks: 'Aufgaben',
    contacts: 'Kontakte',
    calendar: 'Kalender',
    settings: 'Einstellungen',
    
    // Common
    add: 'Hinzufügen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    loading: 'Lädt',
    search: 'Suchen',
    filter: 'Filtern',
    filters: 'Filter',
    show: 'Anzeigen',
    hide: 'Ausblenden',
    clear: 'Löschen',
    clearSearch: 'Suche Löschen',
    filtered: 'gefiltert',
    all: 'Alle',
    allCategories: 'Alle Kategorien',
    completed: 'Abgeschlossen',
    pending: 'Ausstehend',
    active: 'Aktiv',
    overdue: 'Überfällig',
    searchByTitle: 'Nach Titel suchen',
    searchByNameOrPlace: 'Nach Name oder Ort suchen',
    noContactsFound: 'Keine Kontakte gefunden',
    noContactsFoundDesc: 'Keine Kontakte entsprechen Ihrer Suche',
    noContactsYet: 'Noch keine Kontakte',
    addFirstContact: 'Fügen Sie Ihren ersten Kontakt hinzu!',
    
    // Tasks
    addTask: 'Aufgabe Hinzufügen',
    editTask: 'Aufgabe Bearbeiten',
    taskTitle: 'Aufgabentitel',
    taskDetails: 'Aufgabendetails',
    dueDate: 'Fälligkeitsdatum',
    time: 'Zeit',
    category: 'Kategorie',
    priority: 'Priorität',
    location: 'Ort',
    contact: 'Kontakt',
    notes: 'Notizen',
    markComplete: 'Als Erledigt Markieren',
    createFollowUpTask: 'Follow-Up Aufgabe Erstellen',
    enterLocation: 'Ort eingeben',
    
    // Priority levels
    low: 'Niedrig',
    medium: 'Mittel',
    high: 'Hoch',
    
    // Contacts
    addContact: 'Kontakt Hinzufügen',
    editContact: 'Kontakt Bearbeiten',
    contactDetails: 'Kontaktdetails',
    contactName: 'Kontaktname',
    contactNotFound: 'Kontakt Nicht Gefunden',
    contactNotFoundDesc: 'Dieser Kontakt existiert nicht oder wurde gelöscht.',
    goBack: 'Zurück',
    email: 'E-Mail',
    phone: 'Telefon',
    company: 'Unternehmen',
    
    // Settings
    profile: 'Profil',
    preferences: 'Einstellungen',
    categories: 'Kategorien',
    subscription: 'Abonnement',
    support: 'Support',
    
    // Preferences
    pushNotifications: 'Push-Benachrichtigungen',
    emailDigest: 'E-Mail-Zusammenfassung',
    darkMode: 'Dunkler Modus',
    language: 'Sprache',
    notificationDesc: 'Aufgabenerinnerungen und Updates erhalten',
    emailDigestDesc: 'Wöchentliche Zusammenfassung Ihrer Aufgaben',
    darkModeDesc: 'Zu dunklem Design wechseln',
    
    // Profile
    basicInformation: 'Grundinformationen',
    name: 'Name',
    changePassword: 'Passwort Ändern',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort Bestätigen',
    
    // Categories
    myCategories: 'Meine Kategorien',
    addCategory: 'Kategorie Hinzufügen',
    editCategory: 'Kategorie Bearbeiten',
    categoryName: 'Kategoriename',
    color: 'Farbe',
    noCategoriesYet: 'Noch keine Kategorien erstellt',
    createCategoriesToOrganize: 'Erstellen Sie Kategorien, um Ihre Aufgaben zu organisieren',
    
    // Messages
    taskCompleted: 'Aufgabe erfolgreich abgeschlossen!',
    taskDeleted: 'Aufgabe erfolgreich gelöscht!',
    contactSaved: 'Kontakt erfolgreich gespeichert!',
    passwordChanged: 'Passwort erfolgreich geändert!',
    settingsSaved: 'Einstellungen erfolgreich gespeichert!',
    
    // Common UI
    selectCategory: 'Kategorie Auswählen',
    selectContact: 'Kontakt Auswählen',
    today: 'Heute',
    thisWeek: 'Diese Woche',
    next7Days: 'Nächste 7 Tage',
    overdue: 'Überfällig',
    general: 'Allgemein',
    administration: 'Verwaltung',
    dailyBriefing: 'Tägliche Zusammenfassung',
    getBriefing: 'Zusammenfassung Abrufen',
    voiceButton: 'Stimme',
    activeTasks: 'Aktive Aufgaben',
    completedTasks: 'Erledigte Aufgaben',
    createTask: 'Aufgabe Erstellen',
    quickActions: 'Schnellaktionen',
    sendEmail: 'E-Mail Senden',
    call: 'Anrufen',
    allContacts: 'Alle Kontakte',
    noTasksFound: 'Keine Aufgaben gefunden',
    noTasksAssigned: 'Keine Aufgaben diesem Kontakt zugewiesen',
    loadingTasks: 'Aufgaben werden geladen...',
    showingFirstTasks: 'Zeige erste',
    loadAllTasks: 'Alle Aufgaben laden für Suche und Filter',
    addNotes: 'Notizen hinzufügen...',
    completionNotes: 'Abschlussnotizen',
    // Missing UI elements
    loadAllTasks: 'Alle Aufgaben Laden',
    aiPoweredTaskSummary: 'KI-gestützte Aufgabenzusammenfassung',
    generateBriefing: 'Erstellen Sie Ihre tägliche Zusammenfassung',
    goodMorning: 'Guten Morgen',
    goodAfternoon: 'Guten Tag',
    goodEvening: 'Guten Abend',
    nothingScheduled: 'Nichts geplant',
    upcomingTasks: 'Anstehende Aufgaben',
    contactDetails: 'Kontaktdetails',
    personalInfo: 'Persönliche Informationen',
    accountSecurity: 'Konto und Sicherheit',
    systemPreferences: 'Systemeinstellungen',
    helpSupport: 'Hilfe und Support',
    contentManagement: 'Inhaltsverwaltung',
    userManagement: 'Benutzerverwaltung',
    systemConfiguration: 'Systemkonfiguration',
    basicInfo: 'Grundlegende Informationen',
    updateProfile: 'Profil Aktualisieren',
    passwordSecurity: 'Passwort und Sicherheit',
    notificationSettings: 'Benachrichtigungseinstellungen',
    languageRegion: 'Sprache und Region',
    voiceTaskCreator: 'Sprach-Aufgaben-Ersteller',
    speakTaskDetails: 'Sprechen Sie Ihre Aufgabendetails natürlich',
    aiWillHelp: 'KI wird Ihnen beim Ausfüllen der Details helfen',
    
    // Auth
    login: 'Anmelden',
    register: 'Registrieren',
    signIn: 'Anmelden',
    signUp: 'Registrieren',
    forgotPassword: 'Passwort Vergessen',
    resetPassword: 'Passwort Zurücksetzen'
  },
  
  chinese: {
    // Navigation
    tasks: '任务',
    contacts: '联系人',
    calendar: '日历',
    settings: '设置',
    
    // Common
    add: '添加',
    edit: '编辑',
    delete: '删除',
    save: '保存',
    cancel: '取消',
    loading: '加载中',
    search: '搜索',
    filter: '筛选',
    filters: '筛选器',
    show: '显示',
    hide: '隐藏',
    clear: '清除',
    clearSearch: '清除搜索',
    filtered: '已筛选',
    all: '全部',
    allCategories: '所有类别',
    completed: '已完成',
    pending: '待办',
    active: '活跃',
    overdue: '过期',
    searchByTitle: '按标题搜索',
    searchByNameOrPlace: '按姓名或地点搜索',
    noContactsFound: '未找到联系人',
    noContactsFoundDesc: '没有联系人符合您的搜索条件',
    noContactsYet: '还没有联系人',
    addFirstContact: '添加您的第一个联系人开始使用！',
    
    // Tasks
    addTask: '添加任务',
    editTask: '编辑任务',
    taskTitle: '任务标题',
    taskDetails: '任务详情',
    dueDate: '截止日期',
    time: '时间',
    category: '类别',
    priority: '优先级',
    location: '位置',
    contact: '联系人',
    notes: '备注',
    markComplete: '标记完成',
    createFollowUpTask: '创建后续任务',
    enterLocation: '输入位置',
    
    // Priority levels
    low: '低',
    medium: '中',
    high: '高',
    
    // Contacts
    addContact: '添加联系人',
    editContact: '编辑联系人',
    contactDetails: '联系人详情',
    contactName: '联系人姓名',
    contactNotFound: '未找到联系人',
    contactNotFoundDesc: '此联系人不存在或已被删除。',
    goBack: '返回',
    email: '邮箱',
    phone: '电话',
    company: '公司',
    
    // Settings
    profile: '个人资料',
    preferences: '偏好设置',
    categories: '类别',
    subscription: '订阅',
    support: '支持',
    
    // Preferences
    pushNotifications: '推送通知',
    emailDigest: '邮件摘要',
    darkMode: '深色模式',
    language: '语言',
    notificationDesc: '接收任务提醒和更新',
    emailDigestDesc: '每周任务摘要',
    darkModeDesc: '切换到深色主题',
    
    // Profile
    basicInformation: '基本信息',
    name: '姓名',
    changePassword: '更改密码',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmPassword: '确认密码',
    
    // Categories
    myCategories: '我的类别',
    addCategory: '添加类别',
    editCategory: '编辑类别',
    categoryName: '类别名称',
    color: '颜色',
    noCategoriesYet: '还没有创建类别',
    createCategoriesToOrganize: '创建类别来组织您的任务',
    
    // Messages
    taskCompleted: '任务成功完成！',
    taskDeleted: '任务成功删除！',
    contactSaved: '联系人成功保存！',
    passwordChanged: '密码成功更改！',
    settingsSaved: '设置成功保存！',
    
    // Common UI
    selectCategory: '选择类别',
    selectContact: '选择联系人',
    today: '今天',
    thisWeek: '本周',
    next7Days: '未来7天',
    overdue: '过期',
    general: '常规',
    administration: '管理',
    dailyBriefing: '每日简报',
    getBriefing: '获取简报',
    voiceButton: '语音',
    activeTasks: '活跃任务',
    completedTasks: '已完成任务',
    createTask: '创建任务',
    quickActions: '快速操作',
    sendEmail: '发送邮件',
    call: '拨打电话',
    allContacts: '所有联系人',
    noTasksFound: '未找到任务',
    noTasksAssigned: '此联系人没有分配任务',
    loadingTasks: '加载任务中...',
    showingFirstTasks: '显示前',
    loadAllTasks: '加载所有任务以访问搜索和过滤器',
    addNotes: '添加备注...',
    completionNotes: '完成备注',
    // Missing UI elements
    loadAllTasks: '加载所有任务',
    aiPoweredTaskSummary: 'AI驱动的任务摘要',
    generateBriefing: '生成您的每日简报',
    goodMorning: '早上好',
    goodAfternoon: '下午好',
    goodEvening: '晚上好',
    nothingScheduled: '没有安排',
    upcomingTasks: '即将到来的任务',
    contactDetails: '联系人详情',
    personalInfo: '个人信息',
    accountSecurity: '账户和安全',
    systemPreferences: '系统偏好',
    helpSupport: '帮助和支持',
    contentManagement: '内容管理',
    userManagement: '用户管理',
    systemConfiguration: '系统配置',
    basicInfo: '基本信息',
    updateProfile: '更新资料',
    passwordSecurity: '密码和安全',
    notificationSettings: '通知设置',
    languageRegion: '语言和地区',
    voiceTaskCreator: '语音任务创建器',
    speakTaskDetails: '自然地说出您的任务详情',
    aiWillHelp: 'AI将帮助您填写详细信息',
    
    // Auth
    login: '登录',
    register: '注册',
    signIn: '登录',
    signUp: '注册',
    forgotPassword: '忘记密码',
    resetPassword: '重置密码'
  }
};

// Create the language context
const LanguageContext = createContext();

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('english');

  // Load language from localStorage on mount
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem('edotask_language');
      if (savedLanguage && translations[savedLanguage]) {
        setCurrentLanguage(savedLanguage);
      }
    } catch (error) {
      console.error('Error loading language from localStorage:', error);
    }
  }, []); // Empty dependency array to run only once

  // Function to change language
  const changeLanguage = (language) => {
    try {
      if (translations[language]) {
        setCurrentLanguage(language);
        localStorage.setItem('edotask_language', language);
      }
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Function to get translation
  const t = (key, variables = {}) => {
    try {
      let text = translations[currentLanguage][key] || translations['english'][key] || key;
      
      // Replace variables in the text (e.g., {taskTitle}, {contactName})
      Object.keys(variables).forEach(variable => {
        const regex = new RegExp(`{${variable}}`, 'g');
        text = text.replace(regex, variables[variable]);
      });
      
      return text;
    } catch (error) {
      console.error('Error getting translation for key:', key, error);
      return key;
    }
  };

  // Helper function to translate field names
  const translateFieldName = (fieldKey) => {
    console.log('🔍 Translating field:', fieldKey, 'Language:', currentLanguage);
    console.log('🔍 Available fieldNames:', translations[currentLanguage]?.fieldNames);
    const translated = translations[currentLanguage]?.fieldNames?.[fieldKey] || fieldKey.replace(/_/g, ' ');
    console.log('🔍 Translated to:', translated);
    return translated;
  };

  // Helper function to translate multiple field names
  const translateFieldNames = (fieldKeys) => {
    return fieldKeys.map(fieldKey => translateFieldName(fieldKey));
  };

  // Function to get all available languages
  const getAvailableLanguages = () => {
    return [
      { code: 'english', name: 'English' },
      { code: 'spanish', name: 'Español' },
      { code: 'french', name: 'Français' }
    ];
  };

  // Function to get language code for TTS and AI services
  const getLanguageCode = () => {
    const languageMap = {
      'english': 'en',
      'spanish': 'es',
      'french': 'fr',
      'german': 'de',
      'chinese': 'zh'
    };
    return languageMap[currentLanguage] || 'en';
  };

  // Function to get full language name for AI prompts
  const getLanguageName = () => {
    const languageNames = {
      'english': 'English',
      'spanish': 'Spanish',
      'french': 'French',
      'german': 'German',
      'chinese': 'Chinese'
    };
    return languageNames[currentLanguage] || 'English';
  };

  const tWithFallback = useCallback((key, fallback) => {
    const translated = t(key);
    return translated && translated !== key ? translated : fallback;
  }, [currentLanguage]);

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    tWithFallback,
    translateFieldName,
    translateFieldNames,
    getAvailableLanguages,
    getLanguageCode,
    getLanguageName
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;